import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import { todayIso, fmtDateMedium, fmtDateTime, syncDateRange } from '../utils/dateUtils';

// Module-scoped so its useState survives re-renders of the parent.
// Defined inside the parent's render would re-create the component each
// render and reset pagination state on every parent re-render.
function PaginatedTable({ columns, rows, pageSize: initialPageSize = 25, footerRow, theme }) {
  const [pageSize, setPageSize] = React.useState(initialPageSize);
  const [page, setPage] = React.useState(1);

  // Reset to first page when the dataset changes or page size changes.
  React.useEffect(() => { setPage(1); }, [rows, pageSize]);

  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = totalRows === 0 ? 0 : (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalRows);
  const slice = rows.slice(startIdx, endIdx);

  const { tableContainerStyle, tableStyle, thStyle, tdStyle, colors } = theme;
  const cellAlign = (col) => (col.align ? { textAlign: col.align } : null);

  const pageBtn = (disabled) => ({
    padding: '0.35rem 0.6rem',
    border: `1px solid ${colors.border}`,
    background: disabled ? '#f3f4f6' : '#fff',
    color: disabled ? '#9ca3af' : colors.text,
    borderRadius: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 600,
    fontSize: '0.85rem',
    minWidth: '32px',
  });

  return (
    <div>
      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx} style={{ ...thStyle, ...cellAlign(col) }}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {totalRows === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ ...tdStyle, textAlign: 'center', color: colors.textLight }}>
                  No data available
                </td>
              </tr>
            ) : (
              slice.map((row, rIdx) => (
                <tr key={startIdx + rIdx}>
                  {columns.map((col, cIdx) => (
                    <td key={cIdx} style={{ ...tdStyle, ...cellAlign(col) }}>
                      {col.val(row, startIdx + rIdx)}
                    </td>
                  ))}
                </tr>
              ))
            )}
            {/* Pinned footer row (e.g., GRAND TOTAL) — visible on every page */}
            {footerRow}
          </tbody>
        </table>
      </div>
      {totalRows > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.75rem',
          padding: '0.75rem 0.5rem 0.25rem',
          fontSize: '0.85rem',
          color: colors.textLight,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span>
              Showing <strong style={{ color: colors.text }}>{startIdx + 1}-{endIdx}</strong>
              {' of '}
              <strong style={{ color: colors.text }}>{totalRows}</strong>
              {totalRows === 1 ? ' row' : ' rows'}
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              Rows per page:
              <select
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                style={{ padding: '0.25rem 0.4rem', borderRadius: '4px', border: `1px solid ${colors.border}`, fontSize: '0.82rem' }}
              >
                {[10, 25, 50, 100, 250].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <button disabled={safePage <= 1} onClick={() => setPage(1)} style={pageBtn(safePage <= 1)} title="First page">«</button>
            <button disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={pageBtn(safePage <= 1)} title="Previous page">‹</button>
            <span style={{ padding: '0 0.5rem', fontWeight: 600, color: colors.text }}>
              Page {safePage} of {totalPages}
            </span>
            <button disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={pageBtn(safePage >= totalPages)} title="Next page">›</button>
            <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)} style={pageBtn(safePage >= totalPages)} title="Last page">»</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PmsReports({ token, selectedPropertyId, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const colors = {
    primary: '#2c3e50',
    accent: '#3498db',
    success: '#27ae60',
    text: '#2c3e50',
    textLight: '#7f8c8d',
    background: '#ecf0f1',
    cardBg: '#ffffff',
    border: '#bdc3c7',
    headerBg: '#34495e',
    headerText: '#ffffff'
  };

  const [activeTab, setActiveTab] = useState('daily'); // daily, intelligence, corporate, housekeeping, advance, noshow, guesthistory
  const [loading, setLoading] = useState(false);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [error, setError] = useState('');

  const today = todayIso();
  const [date, setDate] = useState(today);
  const [dateRange, setDateRange] = useState({ from: today, to: today });
  const [reservationFilters, setReservationFilters] = useState({
    date_type: 'RESERVATION_DATE',
    from: today,
    to: today,
    reservation_no: '',
    guest_mobile: '',
    corporate_id: '',
    travel_agent_id: ''
  });

  // Data States
  const [dailyOps, setDailyOps] = useState({ arrivals: [], departures: [], inHouse: [] });
  const [occupancy, setOccupancy] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [ratePerformance, setRatePerformance] = useState([]);
  const [segmentRevenue, setSegmentRevenue] = useState([]);
  const [reservationRows, setReservationRows] = useState([]);
  const [corporateOptions, setCorporateOptions] = useState([]);
  const [travelAgentOptions, setTravelAgentOptions] = useState([]);
  const [advanceReport, setAdvanceReport] = useState({ transactions: [], total_advance: 0, total_refund: 0, net: 0 });
  const [advanceLoading, setAdvanceLoading] = useState(false);

  // No-Show & Cancellation
  const [noshowRows, setNoshowRows] = useState([]);
  const [noshowLoading, setNoshowLoading] = useState(false);
  const [noshowFilter, setNoshowFilter] = useState('ALL'); // ALL, CANCELLED, NO_SHOW

  // Guest History
  const [guestHistoryRows, setGuestHistoryRows] = useState([]);
  const [guestHistoryLoading, setGuestHistoryLoading] = useState(false);
  const [guestHistoryFilters, setGuestHistoryFilters] = useState({ guest_name: '', mobile: '' });

  // Corporate & Travel Agent
  const [corporateReport, setCorporateReport] = useState({ corporates: [], travel_agents: [] });
  const [corporateLoading, setCorporateLoading] = useState(false);

  // Sales Team
  const [salesTeamReport, setSalesTeamReport] = useState({ members: [], bookings: [] });
  const [salesTeamLoading, setSalesTeamLoading] = useState(false);
  const [salesTeamOptions, setSalesTeamOptions] = useState([]);
  const [salesMemberFilter, setSalesMemberFilter] = useState('');

  // Housekeeping
  const [hkReport, setHkReport] = useState({ task_summary: [], staff_performance: [], task_list: [], room_status: [] });
  const [hkLoading, setHkLoading] = useState(false);

  // GST Report
  const [gstRows, setGstRows] = useState([]);
  const [gstLoading, setGstLoading] = useState(false);

  // Daily Sales
  const [dailySales, setDailySales] = useState({ rows: [], summary: {} });
  const [dailySalesLoading, setDailySalesLoading] = useState(false);

  // Bill Summary
  const [billSummary, setBillSummary] = useState({ rows: [], summary: {} });
  const [billSummaryLoading, setBillSummaryLoading] = useState(false);
  const [billModeFilter, setBillModeFilter] = useState('');

  // Night Audit Tariff — every ROOM_TARIFF Night Audit posted in the range
  const [nightAuditTariff, setNightAuditTariff] = useState([]);
  const [nightAuditTariffLoading, setNightAuditTariffLoading] = useState(false);

  // Day Summary (Accounting) — single-date grouped report
  const [daySummary, setDaySummary] = useState({
    frontoffice: { rows: [], totals: {} },
    advance: { rows: [], totals: {} },
    fnb: { rows: [], totals: {} },
    refunds: { rows: [], totals: {}, grand_total: 0 },
    grand_total: {},
    net_total: 0,
  });
  // New Refund modal — launched from the Day Summary tab's Refunds section so
  // staff can process a refund without leaving the report. Reuses the exact
  // same POST /reservations/:id/refund endpoint the Reservation screen's own
  // Refund action already calls.
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundReservations, setRefundReservations] = useState([]);
  const [refundReservationsLoading, setRefundReservationsLoading] = useState(false);
  const [refundSearch, setRefundSearch] = useState('');
  const [refundSelected, setRefundSelected] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: '', payment_mode: 'CASH', reference_no: '', remarks: '' });
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [daySummaryLoading, setDaySummaryLoading] = useState(false);

  // Block Room Report
  const [blockRoom, setBlockRoom] = useState({ rows: [], summary: {} });
  const [blockRoomLoading, setBlockRoomLoading] = useState(false);

  // Day Wise Occupancy Analysis
  const [occAnalysis, setOccAnalysis] = useState({ rows: [], totals: {} });
  const [occAnalysisLoading, setOccAnalysisLoading] = useState(false);

  const [folioModal, setFolioModal] = useState({
    open: false,
    loading: false,
    error: '',
    row: null,
    data: null
  });

  // Pre-Check-In link modal state
  const [preCheckinModal, setPreCheckinModal] = useState({ open: false, row: null, sending: false, link: '', fullUrl: '', shortUrl: '', error: '', sent: false });

  const resolvePropertyId = () => String(selectedPropertyId || selectedProperty?.id || localStorage.getItem('selectedPropertyId') || '').trim();
  const resolveChainId = () => String(
    selectedProperty?.chain_id ||
    localStorage.getItem('selectedChainId') ||
    localStorage.getItem('hotelConfigSelectedChainId') ||
    ''
  ).trim();
  const authHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-property-id': resolvePropertyId(),
    ...(resolveChainId() ? { 'x-chain-id': resolveChainId() } : {})
  });

  const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
  const fmtNum = (n) => Number(n || 0).toFixed(2);
  const fmtDate = (value) => fmtDateMedium(value);

  const fetchDailyOps = async () => {
    setLoading(true);
    try {
      const [arrDepRes, occRes, revRes] = await Promise.all([
        fetch(`/api/v1/pms-reports/daily-operations/arrivals-departures?date=${date}`, { headers: authHeaders() }),
        fetch(`/api/v1/pms-reports/daily-operations/occupancy?date=${date}`, { headers: authHeaders() }),
        fetch(`/api/v1/pms-reports/daily-operations/revenue?date=${date}`, { headers: authHeaders() })
      ]);

      const arrDepData = await arrDepRes.json().catch(() => ({}));
      const occData = await occRes.json().catch(() => ({}));
      const revData = await revRes.json().catch(() => ([]));

      if (!arrDepRes.ok) throw new Error(arrDepData.error || 'Failed to load arrivals/departures');

      setDailyOps({
        arrivals: arrDepData.arrivals || [],
        departures: arrDepData.departures || [],
        inHouse: arrDepData.in_house || []
      });
      setOccupancy(occData);
      setRevenue(Array.isArray(revData) ? revData : []);
    } catch (err) {
      setError(err.message || 'Failed to load daily report');
    } finally {
      setLoading(false);
    }
  };

  const fetchIntelligence = async () => {
    setLoading(true);
    try {
      const [rateRes, segRes] = await Promise.all([
        fetch(`/api/v1/pms-reports/intelligence/rate-performance?from=${dateRange.from}&to=${dateRange.to}`, { headers: authHeaders() }),
        fetch(`/api/v1/pms-reports/intelligence/segment-revenue?from=${dateRange.from}&to=${dateRange.to}`, { headers: authHeaders() })
      ]);

      const rateData = await rateRes.json().catch(() => ([]));
      const segData = await segRes.json().catch(() => ([]));

      if (!rateRes.ok) throw new Error(rateData?.error || 'Failed to load rate performance');
      if (!segRes.ok) throw new Error(segData?.error || 'Failed to load segment revenue');

      setRatePerformance(Array.isArray(rateData) ? rateData : []);
      setSegmentRevenue(Array.isArray(segData) ? segData : []);
    } catch (err) {
      setError(err.message || 'Failed to load intelligence report');
    } finally {
      setLoading(false);
    }
  };

  const fetchReservationReport = async (overrideFilters = null) => {
    setReservationLoading(true);
    try {
      const activeFilters = overrideFilters || reservationFilters;
      const params = new URLSearchParams();
      params.set('date_type', activeFilters.date_type || 'RESERVATION_DATE');
      params.set('from', activeFilters.from || today);
      params.set('to', activeFilters.to || activeFilters.from || today);
      if (String(activeFilters.reservation_no || '').trim()) params.set('reservation_no', String(activeFilters.reservation_no).trim());
      if (String(activeFilters.guest_mobile || '').trim()) params.set('guest_mobile', String(activeFilters.guest_mobile).trim());
      if (String(activeFilters.corporate_id || '').trim()) params.set('corporate_id', String(activeFilters.corporate_id).trim());
      if (String(activeFilters.travel_agent_id || '').trim()) params.set('travel_agent_id', String(activeFilters.travel_agent_id).trim());

      const res = await fetch(`/api/v1/pms-reports/daily-operations/reservations?${params.toString()}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || 'Failed to load reservation report');
      setReservationRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load reservation report');
    } finally {
      setReservationLoading(false);
    }
  };

  const fetchReservationMasters = async () => {
    try {
      const [corpRes, taRes] = await Promise.all([
        fetch('/api/v1/pms/corporates?all=1', { headers: authHeaders() }),
        fetch('/api/v1/pms/travel-agents?all=1', { headers: authHeaders() })
      ]);
      const corpData = await corpRes.json().catch(() => ([]));
      const taData = await taRes.json().catch(() => ([]));
      setCorporateOptions(corpRes.ok && Array.isArray(corpData) ? corpData : []);
      setTravelAgentOptions(taRes.ok && Array.isArray(taData) ? taData : []);
    } catch (_) {
      setCorporateOptions([]);
      setTravelAgentOptions([]);
    }
  };

  const openFolioModal = async (row) => {
    const stayId = Number(row?.latest_stay_id || 0);
    if (!Number.isInteger(stayId) || stayId <= 0) return;
    setFolioModal({ open: true, loading: true, error: '', row, data: null });
    try {
      const res = await fetch(`/api/v1/pms/stays/${stayId}/folio`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load folio details');
      setFolioModal({ open: true, loading: false, error: '', row, data });
    } catch (err) {
      setFolioModal({ open: true, loading: false, error: err.message || 'Failed to load folio details', row, data: null });
    }
  };

  const closeFolioModal = () => {
    setFolioModal({ open: false, loading: false, error: '', row: null, data: null });
  };

  const openPreCheckinModal = async (row) => {
    setPreCheckinModal({ open: true, row, sending: false, link: '', fullUrl: '', shortUrl: '', error: '', sent: false, loading: true });
    // Fetch existing token info
    try {
      const res = await fetch(`/api/v1/pms/pre-checkin/status/${row.reservation_id}`, { headers: authHeaders() });
      const data = await res.json();
      const activeToken = (data.tokens || []).find(t => t.status === 'ACTIVE');
      if (activeToken) {
        setPreCheckinModal(prev => ({
          ...prev, loading: false,
          fullUrl: activeToken.full_url || '', shortUrl: activeToken.short_url || '',
          link: activeToken.short_url || activeToken.full_url || '', sent: true
        }));
      } else {
        setPreCheckinModal(prev => ({ ...prev, loading: false }));
      }
    } catch {
      setPreCheckinModal(prev => ({ ...prev, loading: false }));
    }
  };

  const sendPreCheckinFromModal = async () => {
    const resId = Number(preCheckinModal.row?.reservation_id);
    if (!resId) return;
    setPreCheckinModal(prev => ({ ...prev, sending: true, error: '' }));
    try {
      const res = await fetch(`/api/v1/pms/pre-checkin/send/${resId}`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to send');
      const link = data.link || data.short_url || data.full_url || '';
      setPreCheckinModal(prev => ({
        ...prev, sending: false, sent: true, link, fullUrl: data.full_url || '', shortUrl: data.short_url || ''
      }));
      // Update the arrival row's pre_checkin_status locally
      setDailyOps(prev => ({
        ...prev,
        arrivals: prev.arrivals.map(a => a.reservation_id === resId ? { ...a, pre_checkin_status: 'SENT' } : a)
      }));
    } catch (err) {
      setPreCheckinModal(prev => ({ ...prev, sending: false, error: err.message || 'Failed to send' }));
    }
  };

  const closePreCheckinModal = () => {
    setPreCheckinModal({ open: false, row: null, sending: false, link: '', fullUrl: '', shortUrl: '', error: '', sent: false });
  };

  const fetchAdvanceReport = async () => {
    setAdvanceLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.set('from_date', dateRange.from);
      if (dateRange.to) params.set('to_date', dateRange.to);
      const res = await fetch(`/api/v1/pms/advances/report?${params.toString()}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load advance report');
      setAdvanceReport({
        transactions: Array.isArray(data.transactions) ? data.transactions : [],
        total_advance: Number(data.total_advance || 0),
        total_refund: Number(data.total_refund || 0),
        net: Number(data.net || 0)
      });
    } catch (err) {
      setError(err.message || 'Failed to load advance report');
    } finally {
      setAdvanceLoading(false);
    }
  };

  const exportAdvanceCsv = () => {
    const rows = advanceReport.transactions;
    if (!rows.length) return;
    const headers = ['Txn Date', 'Type', 'Reservation No', 'Guest Name', 'Mobile', 'Amount', 'Payment Mode', 'Reference No', 'Remarks', 'Arrival Date', 'Departure Date', 'Reservation Status', 'Created By'];
    const lines = rows.map(r => [
      fmtDateTime(r.txn_date), r.txn_type, r.reservation_no, r.primary_guest_name, r.mobile_number,
      Number(r.amount || 0).toFixed(2), r.payment_mode, r.reference_no || '', r.remarks || '',
      r.arrival_date, r.departure_date, r.reservation_status, r.created_by || ''
    ].map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `advance-report-${dateRange.from}-to-${dateRange.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchNoshowReport = async () => {
    setNoshowLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('from', dateRange.from);
      params.set('to', dateRange.to);
      if (noshowFilter !== 'ALL') params.set('status_filter', noshowFilter);
      const res = await fetch(`/api/v1/pms-reports/daily-operations/cancellations?${params.toString()}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || 'Failed to load no-show/cancellation report');
      setNoshowRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setNoshowLoading(false);
    }
  };

  const fetchGuestHistory = async () => {
    setGuestHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('from', dateRange.from);
      params.set('to', dateRange.to);
      if (guestHistoryFilters.guest_name.trim()) params.set('guest_name', guestHistoryFilters.guest_name.trim());
      if (guestHistoryFilters.mobile.trim()) params.set('mobile', guestHistoryFilters.mobile.trim());
      const res = await fetch(`/api/v1/pms-reports/guest-history?${params.toString()}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || 'Failed to load guest history');
      setGuestHistoryRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setGuestHistoryLoading(false);
    }
  };

  const fetchCorporateReport = async () => {
    setCorporateLoading(true);
    try {
      const res = await fetch(`/api/v1/pms-reports/corporate-report?from=${dateRange.from}&to=${dateRange.to}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load corporate report');
      setCorporateReport({
        corporates: Array.isArray(data.corporates) ? data.corporates : [],
        travel_agents: Array.isArray(data.travel_agents) ? data.travel_agents : []
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setCorporateLoading(false);
    }
  };

  const fetchSalesTeamReport = async () => {
    setSalesTeamLoading(true);
    try {
      const params = new URLSearchParams({ from: dateRange.from, to: dateRange.to });
      if (salesMemberFilter) params.set('member_id', salesMemberFilter);
      const [res, optRes] = await Promise.all([
        fetch(`/api/v1/pms-reports/sales-team-report?${params.toString()}`, { headers: authHeaders() }),
        fetch('/api/v1/pms/sales-team?all=1', { headers: authHeaders() }).catch(() => null)
      ]);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Failed to load sales team report (HTTP ${res.status})`);
      setSalesTeamReport({
        members: Array.isArray(data.members) ? data.members : [],
        bookings: Array.isArray(data.bookings) ? data.bookings : []
      });
      const opts = optRes && optRes.ok ? await optRes.json().catch(() => []) : [];
      setSalesTeamOptions(Array.isArray(opts) ? opts : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setSalesTeamLoading(false);
    }
  };

  const fetchHkReport = async () => {
    setHkLoading(true);
    try {
      const res = await fetch(`/api/v1/pms-reports/housekeeping-report?from=${dateRange.from}&to=${dateRange.to}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load housekeeping report');
      setHkReport({
        task_summary: Array.isArray(data.task_summary) ? data.task_summary : [],
        staff_performance: Array.isArray(data.staff_performance) ? data.staff_performance : [],
        task_list: Array.isArray(data.task_list) ? data.task_list : [],
        room_status: Array.isArray(data.room_status) ? data.room_status : []
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setHkLoading(false);
    }
  };

  const fetchGstReport = async () => {
    setGstLoading(true);
    try {
      const res = await fetch(`/api/v1/pms-reports/daily-operations/tax?from=${dateRange.from}&to=${dateRange.to}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setGstRows(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setGstLoading(false);
    }
  };

  const fetchDailySales = async () => {
    setDailySalesLoading(true);
    try {
      const res = await fetch(`/api/v1/pms-reports/daily-operations/daily-sales?from=${dateRange.from}&to=${dateRange.to}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setDailySales(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setDailySalesLoading(false);
    }
  };

  const fetchBillSummary = async () => {
    setBillSummaryLoading(true);
    try {
      let url = `/api/v1/pms-reports/daily-operations/bill-summary?from=${dateRange.from}&to=${dateRange.to}`;
      if (billModeFilter) url += `&settlement_mode=${encodeURIComponent(billModeFilter)}`;
      const res = await fetch(url, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setBillSummary(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBillSummaryLoading(false);
    }
  };

  const fetchNightAuditTariff = async () => {
    setNightAuditTariffLoading(true);
    try {
      const res = await fetch(
        `/api/v1/pms-reports/daily-operations/night-audit-tariff?from=${dateRange.from}&to=${dateRange.to}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setNightAuditTariff(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setNightAuditTariffLoading(false);
    }
  };

  const fetchDaySummary = async () => {
    setDaySummaryLoading(true);
    try {
      const res = await fetch(
        `/api/v1/pms-reports/daily-operations/day-summary?date=${date}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setDaySummary(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setDaySummaryLoading(false);
    }
  };

  // ── New Refund modal (Day Summary tab) ──────────────────────────────────
  const openRefundModal = () => {
    setRefundModalOpen(true);
    setRefundSelected(null);
    setRefundSearch('');
    setRefundForm({ amount: '', payment_mode: 'CASH', reference_no: '', remarks: '' });
    setRefundError('');
    if (!refundReservations.length) loadRefundReservations();
  };

  const loadRefundReservations = async () => {
    setRefundReservationsLoading(true);
    try {
      const res = await fetch('/api/v1/pms/reservations', { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load reservations');
      setRefundReservations(Array.isArray(data) ? data : []);
    } catch (err) {
      setRefundError(err.message);
    } finally {
      setRefundReservationsLoading(false);
    }
  };

  const refundSearchResults = (() => {
    const q = refundSearch.trim().toLowerCase();
    if (!q) return [];
    return refundReservations
      .filter((r) => Number(r.net_advance || 0) > 0)
      .filter((r) => String(r.reservation_no || '').toLowerCase().includes(q) || String(r.primary_guest_name || '').toLowerCase().includes(q))
      .slice(0, 20);
  })();

  const submitNewRefund = async () => {
    if (!refundSelected?.reservation_id) return;
    const amount = Number(refundForm.amount);
    if (!amount || amount <= 0) { setRefundError('Enter a valid refund amount'); return; }
    if (amount > Number(refundSelected.net_advance || 0)) {
      setRefundError(`Refund amount cannot exceed the net advance held (₹${fmtNum(refundSelected.net_advance)})`);
      return;
    }
    setRefundBusy(true); setRefundError('');
    try {
      const res = await fetch(`/api/v1/pms/reservations/${refundSelected.reservation_id}/refund`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...refundForm, amount })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to record refund');
      setRefundModalOpen(false);
      fetchDaySummary();
    } catch (err) {
      setRefundError(err.message);
    } finally {
      setRefundBusy(false);
    }
  };

  const fetchOccAnalysis = async () => {
    setOccAnalysisLoading(true);
    try {
      const res = await fetch(
        `/api/v1/pms-reports/daily-operations/occupancy-analysis?from=${dateRange.from}&to=${dateRange.to}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setOccAnalysis(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setOccAnalysisLoading(false);
    }
  };

  const exportOccAnalysisCsv = () => {
    if (!occAnalysis.rows.length) return;
    const t = occAnalysis.totals || {};
    exportCsv(
      `day-wise-occupancy-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Date', 'Total', 'Blocked', 'Available', 'Occupied', 'Day Use', 'Sold', '% Occ', 'Pax', 'Revenue', 'ARR', 'ARP'],
      [
        ...occAnalysis.rows.map((r) => [
          r.date, r.total, r.blocked, r.available, r.occupied, r.day_use, r.sold,
          fmtNum(r.pct_occ), r.pax, fmtNum(r.revenue), fmtNum(r.arr), fmtNum(r.arp),
        ]),
        ['GRAND TOTAL', t.total, t.blocked, t.available, t.occupied, t.day_use, t.sold,
         fmtNum(t.pct_occ), t.pax, fmtNum(t.revenue), fmtNum(t.arr), fmtNum(t.arp)],
      ]
    );
  };

  const fetchBlockRoom = async () => {
    setBlockRoomLoading(true);
    try {
      const res = await fetch(
        `/api/v1/pms-reports/daily-operations/block-room?from=${dateRange.from}&to=${dateRange.to}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setBlockRoom(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBlockRoomLoading(false);
    }
  };

  const exportBlockRoomCsv = () => {
    if (!blockRoom.rows.length) return;
    exportCsv(
      `block-room-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Sl.no', 'Room No', 'Room Type', 'From Date', 'To Date', 'Block', 'Remarks', 'Released by', 'Released on', 'Added by', 'Added on', 'Status'],
      blockRoom.rows.map((r, idx) => [
        idx + 1,
        r.room_number || '',
        r.room_type_name || '',
        r.from_date || '',
        r.to_date || '',
        r.block_reason || '',
        r.remarks || '',
        r.released_by_name || '',
        r.released_at || '',
        r.added_by_name || '',
        r.added_at || '',
        r.status || '',
      ])
    );
  };

  const exportDaySummaryCsv = () => {
    const hasAny = (daySummary.frontoffice?.rows?.length || 0)
                 + (daySummary.advance?.rows?.length || 0)
                 + (daySummary.fnb?.rows?.length || 0);
    if (!hasAny) return;
    const header = ['Module', 'Bill Number', 'Bill Date', 'Room', 'Guest Name', 'Cash', 'Credit Card', 'UPI', 'Company', 'Cheque', 'NEFT', 'Company Name'];
    const sectionRows = (label, rows) => (rows || []).map((r) =>
      [label, r.bill_number, r.bill_date, r.room, r.guest_name, fmtNum(r.cash), fmtNum(r.card), fmtNum(r.upi), fmtNum(r.company), fmtNum(r.cheque), fmtNum(r.neft), r.company_name || '']
    );
    const totalRow = (label, t) =>
      [label, '', '', '', '', fmtNum(t?.cash), fmtNum(t?.card), fmtNum(t?.upi), fmtNum(t?.company), fmtNum(t?.cheque), fmtNum(t?.neft), ''];
    const rows = [
      ...sectionRows('Frontoffice', daySummary.frontoffice?.rows),
      totalRow('Frontoffice Total', daySummary.frontoffice?.totals),
      ...sectionRows('Advance', daySummary.advance?.rows),
      totalRow('Advance Total', daySummary.advance?.totals),
      ...sectionRows('F&B Revenue (POS)', daySummary.fnb?.rows),
      totalRow('F&B Total', daySummary.fnb?.totals),
      totalRow('Grand Total', daySummary.grand_total),
    ];
    exportCsv(`day-summary-${date}.csv`, header, rows);
  };

  const exportGstCsv = () => {
    if (!gstRows.length) return;
    exportCsv(`gst-report-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Date', 'Revenue Code', 'Taxable Amount', 'CGST', 'SGST', 'IGST', 'Total Tax'],
      gstRows.map(r => [r.date, r.revenue_code, fmtNum(r.taxable_amount), fmtNum(r.cgst_amount), fmtNum(r.sgst_amount), fmtNum(r.igst_amount), fmtNum(r.total_tax)])
    );
  };

  const exportDailySalesCsv = () => {
    if (!dailySales.rows.length) return;
    exportCsv(`daily-sales-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Date', 'Department', 'Revenue Code', 'Txn Count', 'Amount', 'CGST', 'SGST', 'IGST', 'Tax', 'Total'],
      dailySales.rows.map(r => {
        const amt = parseFloat(r.amount) || 0;
        const tax = parseFloat(r.total_tax) || 0;
        return [r.date, r.source_module, r.revenue_code, r.txn_count, fmtNum(amt), fmtNum(r.cgst), fmtNum(r.sgst), fmtNum(r.igst), fmtNum(tax), fmtNum(amt + tax)];
      })
    );
  };

  const exportBillSummaryCsv = () => {
    if (!billSummary.rows.length) return;
    exportCsv(`bill-summary-${dateRange.from}-to-${dateRange.to}.csv`,
      [
        'Sl.no', 'Bill No', 'Bill Date', 'Reg Num / GRC', 'Room No', 'Guest',
        'Mobile', 'GST No', 'Booking Source', 'Check-In', 'Check-Out',
        'Bill Amount', 'Advance', 'Total Amount',
        'Cash', 'Card', 'UPI', 'Bank Transfer', 'Cheque', 'Company / City Ledger', 'OTA Prepaid', 'Other Pay', 'Other Modes',
        'Amount Received', 'Discounts & Adjustments', 'Moved to Other Folio', 'Refund',
        'Room Charges', 'F&B', 'Other', 'CGST', 'SGST', 'IGST', 'Total Tax',
        'Total Charges', 'Total Credits', 'Balance', 'Settlement Mode', 'Status',
      ],
      billSummary.rows.map((r, idx) => [
        idx + 1,
        r.invoice_no, r.invoice_date, r.reservation_no, r.room_number, r.guest_name,
        r.guest_mobile, r.customer_gstin || '', r.booking_source || '', r.checkin, r.checkout,
        fmtNum(r.bill_amount), fmtNum(r.advance), fmtNum(r.total_payments),
        fmtNum(r.cash), fmtNum(r.card), fmtNum(r.upi), fmtNum(r.bank), fmtNum(r.cheque), fmtNum(r.company), fmtNum(r.ota), fmtNum(r.other_payment), r.other_modes || '',
        fmtNum(r.received), fmtNum(r.adjustments), fmtNum(r.transfers), fmtNum(r.refund),
        fmtNum(r.room_charges), fmtNum(r.fnb_charges), fmtNum(r.other_charges),
        fmtNum(r.cgst), fmtNum(r.sgst), fmtNum(r.igst), fmtNum(r.total_tax),
        fmtNum(r.total_charges), fmtNum(r.total_payments), fmtNum(r.balance),
        r.settlement_mode || '', r.status_label || r.invoice_status || '',
      ])
    );
  };

  const exportNightAuditTariffCsv = () => {
    if (!nightAuditTariff.length) return;
    exportCsv(`night-audit-tariff-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Posting Date', 'Room', 'Res No', 'Guest', 'Rate', 'Tax', 'Total', 'Posted At', 'Stay Status', 'Remarks'],
      nightAuditTariff.map(r => [
        fmtDate(r.posting_date), r.room_number, r.reservation_no, r.primary_guest_name || '',
        fmtNum(r.rate_amount), fmtNum(r.tax_amount), fmtNum(Number(r.rate_amount || 0) + Number(r.tax_amount || 0)),
        r.created_at ? fmtDateTime(r.created_at) : '', r.stay_status || '', r.remarks || '',
      ])
    );
  };

  const exportCorporateCsv = () => {
    const rows = corporateReport.corporates;
    if (!rows.length) return;
    exportCsv(`corporate-report-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Code', 'Corporate Name', 'Contact', 'Bookings', 'Checked In', 'Checked Out', 'Cancelled', 'Room Nights', 'Room Revenue', 'Credit Limit', 'Credit Used'],
      rows.map(r => [r.corporate_code, r.corporate_name, r.contact_person, r.total_bookings, r.checked_in, r.checked_out, r.cancelled, r.total_room_nights, Number(r.total_room_revenue || 0).toFixed(2), Number(r.credit_limit || 0).toFixed(2), Number(r.credit_used || 0).toFixed(2)])
    );
  };

  const exportTravelAgentCsv = () => {
    const rows = corporateReport.travel_agents;
    if (!rows.length) return;
    exportCsv(`travel-agent-report-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Code', 'Agent Name', 'Contact', 'Commission %', 'Bookings', 'Checked In', 'Checked Out', 'Cancelled', 'Room Nights', 'Room Revenue', 'Credit Limit', 'Credit Used'],
      rows.map(r => [r.agent_code, r.agent_name, r.contact_person, r.commission_percent, r.total_bookings, r.checked_in, r.checked_out, r.cancelled, r.total_room_nights, Number(r.total_room_revenue || 0).toFixed(2), Number(r.credit_limit || 0).toFixed(2), Number(r.credit_used || 0).toFixed(2)])
    );
  };

  const exportSalesTeamSummaryCsv = () => {
    const rows = salesTeamReport.members;
    if (!rows.length) return;
    exportCsv(`sales-team-summary-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Member', 'Employee Code', 'Bookings', 'Confirmed', 'Checked In', 'Checked Out', 'Cancelled', 'No-Show', 'Room Nights', 'Room Revenue', 'Tax'],
      rows.map(r => [r.member_name, r.employee_code || '', r.total_bookings, r.confirmed, r.checked_in, r.checked_out, r.cancelled, r.no_show, r.room_nights, Number(r.room_revenue || 0).toFixed(2), Number(r.tax_amount || 0).toFixed(2)])
    );
  };

  const exportSalesTeamBookingsCsv = () => {
    const rows = salesTeamReport.bookings;
    if (!rows.length) return;
    exportCsv(`sales-team-bookings-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Member', 'Res No', 'Guest', 'Mobile', 'Arrival', 'Departure', 'Status', 'Rooms', 'Room Nights', 'Room Revenue', 'Tax', 'Booked On'],
      rows.map(r => [r.member_name, r.reservation_no, r.primary_guest_name || '', r.mobile_number || '', r.arrival_date, r.departure_date, r.status, r.rooms, r.room_nights, Number(r.room_revenue || 0).toFixed(2), Number(r.tax_amount || 0).toFixed(2), r.created_at ? fmtDateTime(r.created_at) : ''])
    );
  };

  const exportHkTasksCsv = () => {
    const rows = hkReport.task_list;
    if (!rows.length) return;
    exportCsv(`housekeeping-tasks-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Date', 'Room', 'Floor', 'Status', 'Priority', 'Staff', 'Source', 'Start', 'End', 'Cleaning Mins', 'Inspection', 'Notes'],
      rows.map(r => [r.task_date, r.room_number, r.floor_no, r.task_status, r.priority, r.assigned_to_name, r.source, fmtDateTime(r.start_at), fmtDateTime(r.end_at), r.cleaning_mins, r.inspection_remarks, r.notes])
    );
  };

  const exportCsv = (filename, headers, rows) => {
    if (!rows.length) return;
    const lines = rows.map(r => r.map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const mealPlanOf = (r) => r.meal_plan_label || r.meal_plan_code || '-';

  const exportArrivals = () => {
    exportCsv(`arrivals-${date}.csv`,
      ['Res No', 'Tot. Rooms', 'Room', 'Room Type', 'Meal Plan', 'Guest', 'Mobile', 'Nights', 'Rate/Night', 'Booking Source', 'Pax', 'Status', 'Departure'],
      dailyOps.arrivals.map(r => [r.reservation_no, r.tot_no_rooms, r.room_number, r.room_type, mealPlanOf(r), r.guest_name, r.mobile, r.nights, r.rate_per_night, r.booking_source, r.pax, r.status, r.departure_date])
    );
  };

  const exportDepartures = () => {
    exportCsv(`departures-${date}.csv`,
      ['Res No', 'Tot. Rooms', 'Room', 'Guest', 'Mobile', 'Check-in', 'Nights', 'Meal Plan', 'Source', 'Status', 'Balance', 'Payment Status'],
      dailyOps.departures.map(r => [r.reservation_no, r.tot_no_rooms, r.room_number, r.guest_name, r.mobile, r.check_in_date, r.nights_stayed, mealPlanOf(r), r.booking_source, r.status, Number(r.balance_due || 0).toFixed(2), r.payment_status])
    );
  };

  const exportInHouse = () => {
    exportCsv(`in-house-${date}.csv`,
      ['Room', 'Room Type', 'Res No', 'Tot. Rooms', 'Guest', 'Mobile', 'Check-in', 'Check-out', 'Total Nights', 'Remaining', 'Rate/Night', 'Meal Plan', 'Folio Balance', 'Source', 'Pax'],
      dailyOps.inHouse.map(r => [r.room_number, r.room_type, r.reservation_no, r.tot_no_rooms, r.guest_name, r.mobile, r.check_in_date, r.check_out_date, r.total_nights, r.nights_remaining, Number(r.rate_per_night || 0).toFixed(2), mealPlanOf(r), Number(r.folio_balance || 0).toFixed(2), r.booking_source, r.pax])
    );
  };

  const exportNoshow = () => {
    exportCsv(`noshow-cancellation-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Res No', 'Tot. Rooms', 'Guest', 'Mobile', 'Status', 'Booking Date', 'Arrival', 'Departure', 'Nights', 'Meal Plan', 'Source', 'Est. Lost Revenue', 'Cancelled At'],
      noshowRows.map(r => [r.reservation_no, r.tot_no_rooms, r.primary_guest_name, r.mobile, r.status, r.booking_date, r.arrival_date, r.departure_date, r.total_nights, mealPlanOf(r), r.booking_source, Number(r.estimated_lost_revenue || 0).toFixed(2), fmtDateTime(r.cancelled_at)])
    );
  };

  const exportGuestHistory = () => {
    exportCsv(`guest-history-${dateRange.from}-to-${dateRange.to}.csv`,
      ['Res No', 'Guest', 'Mobile', 'Email', 'Status', 'Arrival', 'Departure', 'Nights', 'Room Types', 'Rooms', 'Meal Plan', 'Room Charge', 'Source', 'Pax', 'Corporate', 'Agent', 'Booking Date'],
      guestHistoryRows.map(r => [r.reservation_no, r.guest_name, r.mobile, r.email, r.status, r.arrival_date, r.departure_date, r.total_nights, r.room_types, r.room_numbers, mealPlanOf(r), Number(r.total_room_charge || 0).toFixed(2), r.booking_source, r.pax, r.corporate_name, r.travel_agent_name, r.booking_date])
    );
  };

  const exportReservations = () => {
    exportCsv(`reservation-report-${reservationFilters.from || date}-to-${reservationFilters.to || date}.csv`,
      ['Res No', 'Tot. Rooms', 'Guest', 'Status', 'Reservation Date', 'Arrival', 'Departure', 'Actual Check In', 'Actual Check Out', 'Cancellation Time', 'Total Billed Amount', 'GST', 'Meal Plan', 'Pax', 'Booking Source'],
      reservationRows.map(r => [r.reservation_no, r.tot_no_rooms, r.guest_name, r.status, fmtDateTime(r.reservation_datetime), fmtDate(r.arrival_date), fmtDate(r.departure_date), fmtDateTime(r.actual_check_in), fmtDateTime(r.actual_check_out), r.cancellation_time ? fmtDateTime(r.cancellation_time) : '-', Number(r.total_billed_amount || 0).toFixed(2), Number(r.gst_amount || 0).toFixed(2), mealPlanOf(r), Number(r.pax || 0), r.booking_source])
    );
  };

  const refreshDaily = async () => {
    await Promise.all([fetchDailyOps(), fetchReservationReport()]);
  };

  useEffect(() => {
    if (!resolvePropertyId()) return;
    fetchReservationMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedPropertyId, selectedProperty?.id, selectedProperty?.chain_id]);

  useEffect(() => {
    setError('');
    if (activeTab === 'daily') refreshDaily();
    if (activeTab === 'intelligence') fetchIntelligence();
    if (activeTab === 'advance') fetchAdvanceReport();
    if (activeTab === 'noshow') fetchNoshowReport();
    if (activeTab === 'guesthistory') fetchGuestHistory();
    if (activeTab === 'corporate') fetchCorporateReport();
    if (activeTab === 'salesteam') fetchSalesTeamReport();
    if (activeTab === 'housekeeping') fetchHkReport();
    if (activeTab === 'gst') fetchGstReport();
    if (activeTab === 'dailysales') fetchDailySales();
    if (activeTab === 'daysummary') fetchDaySummary();
    if (activeTab === 'blockroom') fetchBlockRoom();
    if (activeTab === 'occanalysis') fetchOccAnalysis();
    if (activeTab === 'billsummary') fetchBillSummary();
    if (activeTab === 'nightaudittariff') fetchNightAuditTariff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, date, dateRange.from, dateRange.to, token, selectedPropertyId, selectedProperty?.id, selectedProperty?.chain_id]);

  useEffect(() => {
    if (activeTab === 'salesteam') fetchSalesTeamReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesMemberFilter]);

  // Styles
  const containerStyle = { padding: isMobile ? '0.75rem' : '1.5rem', backgroundColor: colors.background, minHeight: '100vh', fontFamily: 'Segoe UI, sans-serif', maxWidth: '100%', overflowX: 'hidden' };
  const cardStyle = { background: colors.cardBg, borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '1.5rem', border: `1px solid ${colors.border}`, minWidth: 0, overflow: 'hidden' };
  const headerStyle = { color: colors.primary, marginBottom: '1rem', fontWeight: '700', fontSize: '1.2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
  const tableContainerStyle = { overflowX: 'auto', maxWidth: '100%', borderRadius: '8px', border: `1px solid ${colors.border}` };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' };
  const thStyle = { padding: '0.75rem', textAlign: 'left', background: colors.headerBg, color: colors.headerText, fontWeight: '600', position: 'sticky', top: 0 };
  const tdStyle = { padding: '0.75rem', borderBottom: `1px solid ${colors.border}`, color: colors.text, whiteSpace: 'nowrap' };
  const tabBtnStyle = (active) => ({
    padding: '0.6rem 1.1rem',
    border: 'none',
    background: active ? colors.primary : 'transparent',
    color: active ? '#fff' : colors.text,
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.88rem',
    borderRadius: '8px',
    whiteSpace: 'nowrap'
  });

  // Delegates to the module-scoped PaginatedTable so every grid in this file
  // gets pagination automatically without touching callsites.
  // Optional 3rd arg: { pageSize, footerRow } — pageSize default 25; footerRow
  // is a <tr> pinned below the page slice (used for GRAND TOTAL rows etc.).
  const renderTable = (columns, data, options = {}) => (
    <PaginatedTable
      columns={columns}
      rows={data}
      pageSize={options.pageSize || 25}
      footerRow={options.footerRow}
      theme={{ tableContainerStyle, tableStyle, thStyle, tdStyle, colors }}
    />
  );

  return (
    <div style={containerStyle}>
      <div style={{ marginBottom: isMobile ? '1rem' : '1.25rem' }}>
        <h2 style={{ color: colors.primary, margin: 0, borderBottom: `3px solid ${colors.accent}`, paddingBottom: '0.5rem', fontSize: isMobile ? '1.1rem' : '1.4rem', display: 'inline-block' }}>PMS Reports</h2>
      </div>
      <div style={{ display: 'flex', gap: '0.4rem', background: colors.cardBg, padding: '0.4rem', borderRadius: '8px', flexWrap: 'wrap', marginBottom: isMobile ? '1rem' : '1.5rem', border: `1px solid ${colors.border}` }}>
        {[
          { key: 'daily', label: 'Daily' },
          { key: 'intelligence', label: 'Intelligence' },
          { key: 'noshow', label: 'No-Show & Cancellation' },
          { key: 'guesthistory', label: 'Guest History' },
          { key: 'corporate', label: 'Corporate' },
          { key: 'salesteam', label: 'Sales Team' },
          { key: 'housekeeping', label: 'Housekeeping' },
          { key: 'gst', label: 'GST Report' },
          { key: 'dailysales', label: 'Daily Sales' },
          { key: 'billsummary', label: 'Bill Summary' },
          { key: 'nightaudittariff', label: 'Night Audit Tariff' },
          { key: 'daysummary', label: 'Day Summary' },
          { key: 'occanalysis', label: 'Day Wise Occupancy' },
          { key: 'blockroom', label: 'Block Room' },
          { key: 'advance', label: 'Advance & Refund' }
        ].map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={tabBtnStyle(activeTab === tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      {error && <div className="error-banner" style={{ padding: '1rem', marginBottom: '1rem' }}>{error}</div>}

      {/* Date Controls */}
      <div style={{ ...cardStyle, display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {activeTab === 'daily' || activeTab === 'daysummary' ? (
          <label style={{ fontWeight: '600' }}>
            Date: <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ padding: '0.5rem', borderRadius: '4px', border: `1px solid ${colors.border}` }} />
          </label>
        ) : (
          <>
            <label style={{ fontWeight: '600' }}>
              From: <input type="date" value={dateRange.from} onChange={(e) => setDateRange(syncDateRange({ ...dateRange, from: e.target.value }, 'from', 'to', 'from'))} style={{ padding: '0.5rem', borderRadius: '4px', border: `1px solid ${colors.border}` }} />
            </label>
            <label style={{ fontWeight: '600' }}>
              To: <input type="date" value={dateRange.to} min={dateRange.from || undefined} onChange={(e) => setDateRange(syncDateRange({ ...dateRange, to: e.target.value }, 'from', 'to', 'to'))} style={{ padding: '0.5rem', borderRadius: '4px', border: `1px solid ${colors.border}` }} />
            </label>
          </>
        )}
        <button
          onClick={
            activeTab === 'daily' ? refreshDaily
              : activeTab === 'advance' ? fetchAdvanceReport
              : activeTab === 'noshow' ? fetchNoshowReport
              : activeTab === 'guesthistory' ? fetchGuestHistory
              : activeTab === 'corporate' ? fetchCorporateReport
              : activeTab === 'salesteam' ? fetchSalesTeamReport
              : activeTab === 'housekeeping' ? fetchHkReport
              : activeTab === 'gst' ? fetchGstReport
              : activeTab === 'dailysales' ? fetchDailySales
              : activeTab === 'billsummary' ? fetchBillSummary
              : activeTab === 'nightaudittariff' ? fetchNightAuditTariff
              : activeTab === 'daysummary' ? fetchDaySummary
              : activeTab === 'blockroom' ? fetchBlockRoom
              : activeTab === 'occanalysis' ? fetchOccAnalysis
              : fetchIntelligence
          }
          disabled={loading || reservationLoading || advanceLoading || noshowLoading || guestHistoryLoading || corporateLoading || salesTeamLoading || hkLoading || gstLoading || dailySalesLoading || billSummaryLoading || nightAuditTariffLoading || daySummaryLoading || blockRoomLoading || occAnalysisLoading}
          style={{ padding: '0.5rem 1rem', background: colors.accent, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          {(loading || reservationLoading || advanceLoading || noshowLoading || guestHistoryLoading || gstLoading || dailySalesLoading || billSummaryLoading || nightAuditTariffLoading) ? 'Loading...' : 'Refresh Report'}
        </button>
        {activeTab === 'advance' && advanceReport.transactions.length > 0 && (
          <button onClick={exportAdvanceCsv} style={{ padding: '0.5rem 1rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Export CSV
          </button>
        )}
        {activeTab === 'gst' && gstRows.length > 0 && (
          <button onClick={exportGstCsv} style={{ padding: '0.5rem 1rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Export CSV
          </button>
        )}
        {activeTab === 'dailysales' && dailySales.rows.length > 0 && (
          <button onClick={exportDailySalesCsv} style={{ padding: '0.5rem 1rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Export CSV
          </button>
        )}
        {activeTab === 'billsummary' && billSummary.rows.length > 0 && (
          <>
            <select value={billModeFilter} onChange={e => { setBillModeFilter(e.target.value); }} style={{ padding: '0.5rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}>
              <option value="">All Settlement Modes</option>
              {Object.keys(billSummary.summary.by_settlement_mode || {}).map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <button onClick={exportBillSummaryCsv} style={{ padding: '0.5rem 1rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Export CSV
            </button>
          </>
        )}
        {activeTab === 'nightaudittariff' && nightAuditTariff.length > 0 && (
          <button onClick={exportNightAuditTariffCsv} style={{ padding: '0.5rem 1rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Export CSV
          </button>
        )}
        {activeTab === 'daysummary' && (
          <button onClick={exportDaySummaryCsv} style={{ padding: '0.5rem 1rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Export CSV
          </button>
        )}
        {activeTab === 'blockroom' && blockRoom.rows.length > 0 && (
          <button onClick={exportBlockRoomCsv} style={{ padding: '0.5rem 1rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Export CSV
          </button>
        )}
        {activeTab === 'occanalysis' && occAnalysis.rows.length > 0 && (
          <button onClick={exportOccAnalysisCsv} style={{ padding: '0.5rem 1rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Export CSV
          </button>
        )}
      </div>

      {activeTab === 'daily' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))', gap: isMobile ? '1rem' : '1.5rem' }}>
          {/* Occupancy Card */}
          <div style={cardStyle}>
            <div style={headerStyle}>Daily Occupancy</div>
            {occupancy && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <div style={{ textAlign: 'center', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.9rem', color: colors.textLight }}>Occupancy Rate</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.accent }}>{fmtNum(occupancy.occupancy_percent)}%</div>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.9rem', color: colors.textLight }}>ADR</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.success }}>{fmt(occupancy.adr)}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.9rem', color: colors.textLight }}>RevPAR</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.primary }}>{fmt(occupancy.revpar)}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.9rem', color: colors.textLight }}>Total Revenue</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{fmt(occupancy.room_revenue)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Arrivals */}
          <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
            <div style={headerStyle}>
              <span>Expected Arrivals ({dailyOps.arrivals.length})</span>
              {dailyOps.arrivals.length > 0 && (
                <button onClick={exportArrivals} style={{ padding: '0.4rem 0.8rem', background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem' }}>Export CSV</button>
              )}
            </div>
            {renderTable([
              { label: 'Res No', val: (r) => r.reservation_no || '-' },
              { label: 'Tot. Rooms', val: (r) => r.tot_no_rooms || '-' },
              { label: 'Room', val: (r) => r.room_number || 'Unassigned' },
              { label: 'Room Type', val: (r) => r.room_type || '-' },
              { label: 'Meal Plan', val: (r) => mealPlanOf(r) },
              { label: 'Guest', val: (r) => r.guest_name },
              { label: 'Mobile', val: (r) => r.mobile || '-' },
              { label: 'Nights', val: (r) => r.nights },
              { label: 'Rate/Night', val: (r) => fmt(r.rate_per_night) },
              { label: 'Source', val: (r) => r.booking_source || '-' },
              { label: 'Pax', val: (r) => r.pax || '-' },
              { label: 'Status', val: (r) => r.status || 'EXPECTED' },
              { label: 'Departure', val: (r) => fmtDate(r.departure_date) },
              {
                label: 'Pre-CI Link',
                val: (r) => {
                  const status = String(r.pre_checkin_status || 'NOT_SENT').toUpperCase();
                  const canSend = ['TENTATIVE', 'CONFIRMED', 'GUARANTEED', 'RESERVED'].includes(String(r.status || '').toUpperCase());
                  if (!canSend) return <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>-</span>;
                  return (
                    <button
                      onClick={() => openPreCheckinModal(r)}
                      style={{
                        border: 'none', borderRadius: 4, padding: '0.25rem 0.6rem', fontSize: '0.73rem', fontWeight: 600,
                        cursor: 'pointer', whiteSpace: 'nowrap',
                        background: status === 'SENT' || status === 'COMPLETED' ? '#dbeafe' : '#f0fdf4',
                        color: status === 'SENT' || status === 'COMPLETED' ? '#1d4ed8' : '#059669',
                      }}
                    >
                      {status === 'NOT_SENT' ? 'Send Link' : status === 'COMPLETED' ? 'View Link' : 'View / Resend'}
                    </button>
                  );
                }
              }
            ], dailyOps.arrivals)}
          </div>

          {/* Departures */}
          <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
            <div style={headerStyle}>
              <span>Expected Departures ({dailyOps.departures.length})</span>
              {dailyOps.departures.length > 0 && (
                <button onClick={exportDepartures} style={{ padding: '0.4rem 0.8rem', background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem' }}>Export CSV</button>
              )}
            </div>
            {renderTable([
              { label: 'Res No', val: (r) => r.reservation_no || '-' },
              { label: 'Tot. Rooms', val: (r) => r.tot_no_rooms || '-' },
              { label: 'Room', val: (r) => r.room_number || '-' },
              { label: 'Guest', val: (r) => r.guest_name },
              { label: 'Mobile', val: (r) => r.mobile || '-' },
              { label: 'Check-in', val: (r) => fmtDate(r.check_in_date) },
              { label: 'Nights', val: (r) => r.nights_stayed },
              { label: 'Meal Plan', val: (r) => mealPlanOf(r) },
              { label: 'Source', val: (r) => r.booking_source || '-' },
              { label: 'Status', val: (r) => r.status },
              { label: 'Balance Due', val: (r) => fmt(r.balance_due) },
              {
                label: 'Payment',
                val: (r) => (
                  <span style={{
                    padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700,
                    background: r.payment_status === 'SETTLED' ? '#dcfce7' : '#fef9c3',
                    color: r.payment_status === 'SETTLED' ? '#15803d' : '#a16207',
                    border: `1px solid ${r.payment_status === 'SETTLED' ? '#bbf7d0' : '#fde68a'}`
                  }}>{r.payment_status}</span>
                )
              }
            ], dailyOps.departures)}
          </div>

          {/* In House */}
          <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
            <div style={headerStyle}>
              <span>In-House Guests ({dailyOps.inHouse.length})</span>
              {dailyOps.inHouse.length > 0 && (
                <button onClick={exportInHouse} style={{ padding: '0.4rem 0.8rem', background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem' }}>Export CSV</button>
              )}
            </div>
            {renderTable([
              { label: 'Room', val: (r) => r.room_number },
              { label: 'Type', val: (r) => r.room_type || '-' },
              { label: 'Meal Plan', val: (r) => mealPlanOf(r) },
              { label: 'Res No', val: (r) => r.reservation_no || '-' },
              { label: 'Tot. Rooms', val: (r) => r.tot_no_rooms || '-' },
              { label: 'Guest', val: (r) => r.guest_name },
              { label: 'Mobile', val: (r) => r.mobile || '-' },
              { label: 'Check-in', val: (r) => fmtDate(r.check_in_date) },
              { label: 'Check-out', val: (r) => fmtDate(r.check_out_date) },
              { label: 'Nights', val: (r) => r.total_nights },
              { label: 'Remaining', val: (r) => r.nights_remaining },
              { label: 'Rate/Night', val: (r) => fmt(r.rate_per_night) },
              { label: 'Folio Bal.', val: (r) => fmt(r.folio_balance) },
              { label: 'Source', val: (r) => r.booking_source || '-' },
              { label: 'Pax', val: (r) => r.pax || '-' }
            ], dailyOps.inHouse)}
          </div>

          {/* Daily Revenue Breakdown */}
          <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
            <div style={headerStyle}>Daily Revenue Breakdown</div>
            {renderTable([
              { label: 'Posting Type', val: (r) => r.posting_type },
              { label: 'Count', val: (r) => r.count },
              { label: 'Amount', val: (r) => fmt(r.amount) },
              { label: 'Tax', val: (r) => fmt(Number(r.cgst) + Number(r.sgst) + Number(r.igst)) },
              { label: 'Total', val: (r) => fmt(r.total) }
            ], revenue)}
          </div>

          {/* Reservation Report */}
          <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
            <div style={headerStyle}>
              <span>Reservation Report ({reservationRows.length})</span>
              {reservationRows.length > 0 && (
                <button onClick={exportReservations} style={{ padding: '0.4rem 0.8rem', background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem' }}>Export CSV</button>
              )}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '0.6rem',
              marginBottom: '0.9rem'
            }}>
              <label style={{ fontSize: '0.82rem', color: colors.text }}>
                Date Type
                <select
                  value={reservationFilters.date_type}
                  onChange={(e) => setReservationFilters((prev) => ({ ...prev, date_type: e.target.value }))}
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
                >
                  <option value="RESERVATION_DATE">Reservation Date</option>
                  <option value="ARRIVAL_DATE">Arrival Date</option>
                  <option value="DEPARTURE_DATE">Departure Date</option>
                  <option value="ACTUAL_CHECK_IN_DATE">Actual Check In Date</option>
                  <option value="ACTUAL_CHECK_OUT_DATE">Actual Check Out Date</option>
                </select>
              </label>
              <label style={{ fontSize: '0.82rem', color: colors.text }}>
                From Date
                <input
                  type="date"
                  value={reservationFilters.from}
                  onChange={(e) => setReservationFilters((prev) => syncDateRange({ ...prev, from: e.target.value }, 'from', 'to', 'from'))}
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
                />
              </label>
              <label style={{ fontSize: '0.82rem', color: colors.text }}>
                To Date
                <input
                  type="date"
                  value={reservationFilters.to}
                  min={reservationFilters.from || undefined}
                  onChange={(e) => setReservationFilters((prev) => syncDateRange({ ...prev, to: e.target.value }, 'from', 'to', 'to'))}
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
                />
              </label>
              <label style={{ fontSize: '0.82rem', color: colors.text }}>
                Reservation No
                <input
                  type="text"
                  value={reservationFilters.reservation_no}
                  onChange={(e) => setReservationFilters((prev) => ({ ...prev, reservation_no: e.target.value }))}
                  placeholder="RES-38 or 38"
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
                />
              </label>
              <label style={{ fontSize: '0.82rem', color: colors.text }}>
                Guest Mobile
                <input
                  type="text"
                  value={reservationFilters.guest_mobile}
                  onChange={(e) => setReservationFilters((prev) => ({ ...prev, guest_mobile: e.target.value }))}
                  placeholder="Mobile number"
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
                />
              </label>
              <label style={{ fontSize: '0.82rem', color: colors.text }}>
                Corporate Name
                <select
                  value={reservationFilters.corporate_id}
                  onChange={(e) => setReservationFilters((prev) => ({ ...prev, corporate_id: e.target.value }))}
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
                >
                  <option value="">All Corporates</option>
                  {corporateOptions.map((c) => (
                    <option key={c.corporate_id} value={c.corporate_id}>{c.corporate_name}</option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: '0.82rem', color: colors.text }}>
                Travel Agent Name
                <select
                  value={reservationFilters.travel_agent_id}
                  onChange={(e) => setReservationFilters((prev) => ({ ...prev, travel_agent_id: e.target.value }))}
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
                >
                  <option value="">All Agents</option>
                  {travelAgentOptions.map((t) => (
                    <option key={t.travel_agent_id} value={t.travel_agent_id}>{t.agent_name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.9rem' }}>
              <button
                onClick={() => fetchReservationReport()}
                disabled={reservationLoading}
                style={{ padding: '0.5rem 0.9rem', borderRadius: '4px', border: 'none', background: colors.accent, color: '#fff', cursor: 'pointer' }}
              >
                {reservationLoading ? 'Loading...' : 'Search'}
              </button>
              <button
                onClick={() => {
                  const resetFilters = {
                    date_type: 'RESERVATION_DATE',
                    from: today,
                    to: today,
                    reservation_no: '',
                    guest_mobile: '',
                    corporate_id: '',
                    travel_agent_id: ''
                  };
                  setReservationFilters(resetFilters);
                  fetchReservationReport(resetFilters);
                }}
                style={{ padding: '0.5rem 0.9rem', borderRadius: '4px', border: `1px solid ${colors.accent}`, background: '#e8f0fe', color: colors.accent, fontWeight: 600, cursor: 'pointer' }}
              >
                Reset
              </button>
            </div>
            {renderTable([
              { label: 'Res No', val: (r) => r.reservation_no || '-' },
              { label: 'Tot. Rooms', val: (r) => r.tot_no_rooms || '-' },
              { label: 'Guest', val: (r) => r.guest_name || '-' },
              { label: 'Status', val: (r) => r.status || '-' },
              { label: 'Reservation Date', val: (r) => fmtDateTime(r.reservation_datetime) },
              { label: 'Arrival', val: (r) => fmtDate(r.arrival_date) },
              { label: 'Departure', val: (r) => fmtDate(r.departure_date) },
              { label: 'Actual Check In', val: (r) => fmtDateTime(r.actual_check_in) },
              { label: 'Actual Check Out', val: (r) => fmtDateTime(r.actual_check_out) },
              { label: 'Cancellation Time', val: (r) => r.cancellation_time ? fmtDateTime(r.cancellation_time) : '-' },
              { label: 'Total Billed Amount', val: (r) => fmt(r.total_billed_amount) },
              { label: 'GST', val: (r) => fmt(r.gst_amount) },
              { label: 'Meal Plan', val: (r) => mealPlanOf(r) },
              { label: 'Pax', val: (r) => Number(r.pax || 0) },
              { label: 'Booking Source', val: (r) => r.booking_source || '-' },
              {
                label: 'Folio Link',
                val: (r) => (
                  r.latest_stay_id
                    ? (
                      <button
                        onClick={() => openFolioModal(r)}
                        style={{ border: 'none', background: 'transparent', color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                      >
                        View Folio
                      </button>
                    )
                    : '-'
                )
              }
            ], reservationRows)}
          </div>
        </div>
      )}

      {activeTab === 'intelligence' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
          <div style={cardStyle}>
            <div style={headerStyle}>Rate Plan Performance</div>
            {renderTable([
              { label: 'Rate Plan', val: (r) => r.plan_name },
              { label: 'Reservations', val: (r) => r.reservations_count },
              { label: 'Room Nights', val: (r) => r.room_nights },
              { label: 'Total Revenue', val: (r) => fmt(r.total_revenue) },
              { label: 'Avg Rate', val: (r) => (Number(r.room_nights || 0) > 0 ? fmt(Number(r.total_revenue || 0) / Number(r.room_nights || 1)) : '-') }
            ], ratePerformance)}
          </div>

          <div style={cardStyle}>
            <div style={headerStyle}>Segment Revenue</div>
            {renderTable([
              { label: 'Segment', val: (r) => r.segment || 'Unknown' },
              { label: 'Reservations', val: (r) => r.reservations },
              { label: 'Revenue', val: (r) => fmt(r.revenue) }
            ], segmentRevenue)}
          </div>
        </div>
      )}

      {activeTab === 'corporate' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {corporateLoading && <div style={{ ...cardStyle, textAlign: 'center', color: colors.textLight }}>Loading...</div>}

          {/* Corporate Summary */}
          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>Corporate Booking Summary</span>
              <button onClick={exportCorporateCsv} disabled={!corporateReport.corporates.length} style={{ padding: '0.4rem 0.8rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.82rem' }}>Export CSV</button>
            </div>
            {renderTable([
              { label: 'Code', val: (r) => r.corporate_code },
              { label: 'Corporate Name', val: (r) => r.corporate_name },
              { label: 'Contact', val: (r) => r.contact_person || '-' },
              { label: 'Bookings', val: (r) => r.total_bookings },
              { label: 'Checked In', val: (r) => r.checked_in },
              { label: 'Checked Out', val: (r) => r.checked_out },
              { label: 'Cancelled', val: (r) => r.cancelled },
              { label: 'Room Nights', val: (r) => r.total_room_nights },
              { label: 'Revenue', val: (r) => fmt(r.total_room_revenue) },
              { label: 'Credit Limit', val: (r) => fmt(r.credit_limit) },
              { label: 'Credit Used', val: (r) => fmt(r.credit_used) },
              { label: 'Available', val: (r) => fmt(Number(r.credit_limit || 0) - Number(r.credit_used || 0)) }
            ], corporateReport.corporates)}
          </div>

          {/* Travel Agent Summary */}
          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>Travel Agent Booking Summary</span>
              <button onClick={exportTravelAgentCsv} disabled={!corporateReport.travel_agents.length} style={{ padding: '0.4rem 0.8rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.82rem' }}>Export CSV</button>
            </div>
            {renderTable([
              { label: 'Code', val: (r) => r.agent_code },
              { label: 'Agent Name', val: (r) => r.agent_name },
              { label: 'Contact', val: (r) => r.contact_person || '-' },
              { label: 'Commission %', val: (r) => fmtNum(r.commission_percent) },
              { label: 'Bookings', val: (r) => r.total_bookings },
              { label: 'Checked In', val: (r) => r.checked_in },
              { label: 'Checked Out', val: (r) => r.checked_out },
              { label: 'Cancelled', val: (r) => r.cancelled },
              { label: 'Room Nights', val: (r) => r.total_room_nights },
              { label: 'Revenue', val: (r) => fmt(r.total_room_revenue) },
              { label: 'Credit Limit', val: (r) => fmt(r.credit_limit) },
              { label: 'Credit Used', val: (r) => fmt(r.credit_used) }
            ], corporateReport.travel_agents)}
          </div>
        </div>
      )}

      {activeTab === 'salesteam' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {salesTeamLoading && <div style={{ ...cardStyle, textAlign: 'center', color: colors.textLight }}>Loading...</div>}

          <div style={{ ...cardStyle, display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: 0 }}>
            <label style={{ fontWeight: '600' }}>
              Sales Team Member:{' '}
              <select value={salesMemberFilter} onChange={(e) => setSalesMemberFilter(e.target.value)} style={{ padding: '0.5rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}>
                <option value="">All Members</option>
                {salesTeamOptions.map((m) => (
                  <option key={m.member_id} value={m.member_id}>
                    {m.member_name}{m.employee_code ? ` (${m.employee_code})` : ''}{m.is_active ? '' : ' — inactive'}
                  </option>
                ))}
              </select>
            </label>
            <span style={{ color: colors.textLight, fontSize: '0.82rem' }}>
              Bookings with Booking Source = Sales Team whose stay falls in the date range. Revenue is net room charge; cancelled and no-show bookings are excluded from totals.
            </span>
          </div>

          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>Sales Team Performance Summary</span>
              <button onClick={exportSalesTeamSummaryCsv} disabled={!salesTeamReport.members.length} style={{ padding: '0.4rem 0.8rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.82rem' }}>Export CSV</button>
            </div>
            {renderTable([
              { label: 'Member', val: (r) => r.member_name },
              { label: 'Emp Code', val: (r) => r.employee_code || '-' },
              { label: 'Bookings', val: (r) => r.total_bookings },
              { label: 'Confirmed', val: (r) => r.confirmed },
              { label: 'Checked In', val: (r) => r.checked_in },
              { label: 'Checked Out', val: (r) => r.checked_out },
              { label: 'Cancelled', val: (r) => r.cancelled },
              { label: 'No-Show', val: (r) => r.no_show },
              { label: 'Room Nights', val: (r) => r.room_nights },
              { label: 'Room Revenue', val: (r) => fmt(r.room_revenue) },
              { label: 'Tax', val: (r) => fmt(r.tax_amount) }
            ], salesTeamReport.members)}
          </div>

          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>Sales Team Bookings</span>
              <button onClick={exportSalesTeamBookingsCsv} disabled={!salesTeamReport.bookings.length} style={{ padding: '0.4rem 0.8rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.82rem' }}>Export CSV</button>
            </div>
            {renderTable([
              { label: 'Member', val: (r) => r.member_name },
              { label: 'Res No', val: (r) => r.reservation_no },
              { label: 'Guest', val: (r) => r.primary_guest_name || '-' },
              { label: 'Mobile', val: (r) => r.mobile_number || '-' },
              { label: 'Arrival', val: (r) => fmtDate(r.arrival_date) },
              { label: 'Departure', val: (r) => fmtDate(r.departure_date) },
              { label: 'Status', val: (r) => r.status },
              { label: 'Rooms', val: (r) => r.rooms },
              { label: 'Room Nights', val: (r) => r.room_nights },
              { label: 'Room Revenue', val: (r) => fmt(r.room_revenue) },
              { label: 'Tax', val: (r) => fmt(r.tax_amount) },
              { label: 'Booked On', val: (r) => (r.created_at ? fmtDateTime(r.created_at) : '-') }
            ], salesTeamReport.bookings)}
          </div>
        </div>
      )}

      {activeTab === 'housekeeping' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {hkLoading &&<div style={{ ...cardStyle, textAlign: 'center', color: colors.textLight }}>Loading...</div>}

          {/* Summary Cards Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
            {/* Room HK Status Snapshot */}
            {hkReport.room_status.map((s) => (
              <div key={s.housekeeping_status} style={{ ...cardStyle, marginBottom: 0, textAlign: 'center' }}>
                <div style={{ fontSize: '0.78rem', color: colors.textLight, textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.3rem' }}>{s.housekeeping_status || 'Unknown'}</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.housekeeping_status === 'CLEAN' ? colors.success : s.housekeeping_status === 'DIRTY' ? '#e74c3c' : colors.accent }}>{s.count}</div>
                <div style={{ fontSize: '0.75rem', color: colors.textLight }}>rooms</div>
              </div>
            ))}
            {/* Task Status Summary */}
            {hkReport.task_summary.map((s) => (
              <div key={s.task_status} style={{ ...cardStyle, marginBottom: 0, textAlign: 'center' }}>
                <div style={{ fontSize: '0.78rem', color: colors.textLight, textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.3rem' }}>{s.task_status}</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.task_status === 'COMPLETED' || s.task_status === 'APPROVED' ? colors.success : s.task_status === 'REJECTED' ? '#e74c3c' : colors.accent }}>{s.count}</div>
                <div style={{ fontSize: '0.75rem', color: colors.textLight }}>{s.rooms} rooms</div>
              </div>
            ))}
          </div>

          {/* Staff Performance */}
          <div style={cardStyle}>
            <div style={headerStyle}>Staff Performance</div>
            {renderTable([
              { label: 'Staff Name', val: (r) => r.staff_name || '-' },
              { label: 'Total Tasks', val: (r) => r.total_tasks },
              { label: 'Completed', val: (r) => r.completed },
              { label: 'Approved', val: (r) => r.approved },
              { label: 'Rejected', val: (r) => r.rejected },
              { label: 'Pending', val: (r) => r.pending },
              { label: 'Avg Clean (min)', val: (r) => r.avg_cleaning_mins != null ? r.avg_cleaning_mins : '-' },
              { label: 'Completion %', val: (r) => {
                const total = Number(r.total_tasks || 0);
                const done = Number(r.completed || 0) + Number(r.approved || 0);
                return total > 0 ? Math.round((done / total) * 100) + '%' : '-';
              }}
            ], hkReport.staff_performance)}
          </div>

          {/* Task Detail List */}
          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>Task Details ({hkReport.task_list.length})</span>
              <button onClick={exportHkTasksCsv} disabled={!hkReport.task_list.length} style={{ padding: '0.4rem 0.8rem', background: colors.success, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.82rem' }}>Export CSV</button>
            </div>
            {renderTable([
              { label: 'Date', val: (r) => fmtDate(r.task_date) },
              { label: 'Room', val: (r) => r.room_number },
              { label: 'Floor', val: (r) => r.floor_no || '-' },
              { label: 'Status', val: (r) => r.task_status },
              { label: 'Priority', val: (r) => r.priority },
              { label: 'Staff', val: (r) => r.assigned_to_name || 'Unassigned' },
              { label: 'Source', val: (r) => r.source || '-' },
              { label: 'Start', val: (r) => fmtDateTime(r.start_at) },
              { label: 'End', val: (r) => fmtDateTime(r.end_at) },
              { label: 'Cleaning (min)', val: (r) => r.cleaning_mins != null ? r.cleaning_mins : '-' },
              { label: 'Inspection', val: (r) => r.inspection_remarks || '-' },
              { label: 'Notes', val: (r) => r.notes || '-' }
            ], hkReport.task_list)}
          </div>
        </div>
      )}

      {/* ── GST Report Tab ──────────────────────────────────────── */}
      {activeTab === 'gst' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {gstLoading && <div style={{ ...cardStyle, textAlign: 'center', color: colors.textLight }}>Loading...</div>}
          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>GST / Tax Report ({gstRows.length} rows)</span>
            </div>
            {renderTable([
              { label: 'Date', val: (r) => fmtDate(r.date) },
              { label: 'Revenue Code', val: (r) => r.revenue_code },
              { label: 'Taxable Amount', val: (r) => fmt(r.taxable_amount) },
              { label: 'CGST', val: (r) => fmt(r.cgst_amount) },
              { label: 'SGST', val: (r) => fmt(r.sgst_amount) },
              { label: 'IGST', val: (r) => fmt(r.igst_amount) },
              { label: 'Total Tax', val: (r) => fmt(r.total_tax) }
            ], gstRows)}
            {gstRows.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                {[
                  { label: 'Total Taxable', value: fmt(gstRows.reduce((s, r) => s + parseFloat(r.taxable_amount || 0), 0)), color: colors.primary },
                  { label: 'Total CGST', value: fmt(gstRows.reduce((s, r) => s + parseFloat(r.cgst_amount || 0), 0)), color: colors.accent },
                  { label: 'Total SGST', value: fmt(gstRows.reduce((s, r) => s + parseFloat(r.sgst_amount || 0), 0)), color: colors.accent },
                  { label: 'Total IGST', value: fmt(gstRows.reduce((s, r) => s + parseFloat(r.igst_amount || 0), 0)), color: '#8e44ad' },
                  { label: 'Total Tax', value: fmt(gstRows.reduce((s, r) => s + parseFloat(r.total_tax || 0), 0)), color: colors.success }
                ].map((s) => (
                  <div key={s.label} style={{ background: '#fff', borderRadius: '8px', padding: '1rem', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.78rem', color: colors.textLight, marginBottom: '0.3rem' }}>{s.label}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Daily Sales Tab ──────────────────────────────────────── */}
      {activeTab === 'dailysales' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {dailySalesLoading && <div style={{ ...cardStyle, textAlign: 'center', color: colors.textLight }}>Loading...</div>}

          {/* Summary Cards */}
          {dailySales.summary && dailySales.summary.gross_revenue !== undefined && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
              {[
                { label: 'Room Revenue', value: fmt(dailySales.summary.room_revenue), color: '#1d4ed8' },
                { label: 'F&B Revenue', value: fmt(dailySales.summary.fnb_revenue), color: '#059669' },
                { label: 'Bar Revenue', value: fmt(dailySales.summary.bar_revenue), color: '#7c3aed' },
                { label: 'Other Revenue', value: fmt(dailySales.summary.other_revenue), color: '#d97706' },
                { label: 'Allowances', value: fmt(dailySales.summary.allowances), color: '#dc2626' },
                { label: 'Gross Revenue', value: fmt(dailySales.summary.gross_revenue), color: colors.primary },
                { label: 'Total Tax', value: fmt(dailySales.summary.total_tax), color: '#6b7280' },
                { label: 'Net Revenue', value: fmt(dailySales.summary.net_revenue), color: colors.success }
              ].map((s) => (
                <div key={s.label} style={{ background: '#fff', borderRadius: '8px', padding: '1rem', border: `1px solid ${colors.border}`, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '0.78rem', color: colors.textLight, marginBottom: '0.3rem' }}>{s.label}</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Detail Table */}
          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>Daily Sales Detail ({dailySales.rows.length} rows)</span>
            </div>
            {renderTable([
              { label: 'Date', val: (r) => fmtDate(r.date) },
              { label: 'Department', val: (r) => r.source_module },
              { label: 'Type', val: (r) => r.posting_type },
              { label: 'Revenue Code', val: (r) => r.revenue_code },
              { label: 'Txn Count', val: (r) => r.txn_count },
              { label: 'Amount', val: (r) => fmt(r.amount) },
              { label: 'CGST', val: (r) => fmt(r.cgst) },
              { label: 'SGST', val: (r) => fmt(r.sgst) },
              { label: 'IGST', val: (r) => fmt(r.igst) },
              { label: 'Tax', val: (r) => fmt(r.total_tax) },
              { label: 'Total', val: (r) => fmt(parseFloat(r.amount || 0) + parseFloat(r.total_tax || 0)) }
            ], dailySales.rows)}
          </div>
        </div>
      )}

      {/* ── Bill Summary Tab ──────────────────────────────────────── */}
      {activeTab === 'billsummary' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {billSummaryLoading && <div style={{ ...cardStyle, textAlign: 'center', color: colors.textLight }}>Loading...</div>}

          {/* Summary Cards */}
          {billSummary.summary && billSummary.summary.total_invoices !== undefined && (
            <>
              {(() => {
                const sm = billSummary.summary;
                const num = (v) => Number(v || 0);
                const tile = (t, big) => (
                  <div key={t.label} title={t.hint || ''} style={{ background: '#fff', borderRadius: '8px', padding: big ? '1rem' : '0.75rem', border: `1px solid ${colors.border}`, textAlign: 'center', boxShadow: big ? '0 1px 3px rgba(0,0,0,0.06)' : 'none' }}>
                    <div style={{ fontSize: big ? '0.78rem' : '0.74rem', color: colors.textLight, marginBottom: '0.2rem' }}>{t.label}</div>
                    <div style={{ fontSize: big ? '1.25rem' : '1.05rem', fontWeight: 700, color: t.color }}>{t.value}</div>
                    {t.sub ? <div style={{ fontSize: '0.7rem', color: colors.textLight, marginTop: '0.2rem' }}>{t.sub}</div> : null}
                  </div>
                );
                const sectionHead = (title, note) => (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: colors.primary }}>{title}</div>
                    {note ? <div style={{ fontSize: '0.76rem', color: colors.textLight }}>{note}</div> : null}
                  </div>
                );
                // Every credit on a bill is money received, an adjustment, or moved
                // to another folio; the three must add up to the credits stored on the bills.
                const adjustments = num(sm.total_adjustments) + num(sm.total_transfers);
                const gap = Math.round((num(sm.total_payments) - num(sm.total_received) - adjustments) * 100) / 100;
                const modes = [
                  { label: 'Cash', value: sm.total_cash, color: '#16a34a' },
                  { label: 'Card', value: sm.total_card, color: '#2563eb' },
                  { label: 'UPI', value: sm.total_upi, color: '#7c3aed' },
                  { label: 'Bank Transfer', value: sm.total_bank, color: '#0369a1', hint: 'NEFT / RTGS / IMPS / bank transfer' },
                  { label: 'Cheque', value: sm.total_cheque, color: '#0891b2' },
                  { label: 'Company / City Ledger', value: sm.total_company, color: '#0e7490', hint: 'Billed to a company or moved to city ledger: not yet received' },
                  { label: 'OTA Prepaid', value: sm.total_ota, color: '#9333ea', hint: 'Collected by the OTA' },
                  { label: 'Other', value: sm.total_other_pay, color: '#475569', sub: (sm.other_modes || []).join(', ') },
                ].filter((t) => ['Cash', 'Card', 'UPI'].includes(t.label) || num(t.value) !== 0);
                return (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                      {[
                        { label: 'Total Invoices', value: sm.total_invoices, color: colors.primary },
                        { label: 'Total Bill Value', value: fmt(sm.total_charges), color: '#1d4ed8', hint: 'All charges on these bills, incl. tax' },
                        { label: 'Amount Received', value: fmt(sm.total_received), color: colors.success, hint: 'Money received against these bills in all modes, incl. advances' },
                        { label: 'Discounts & Adjustments', value: fmt(adjustments), color: '#b45309', hint: 'Discounts, voids, credit notes and charges moved to another folio: not money received' },
                        { label: 'Outstanding', value: fmt(sm.total_balance), color: sm.total_balance > 0 ? '#dc2626' : colors.success },
                        { label: 'Total Tax', value: fmt(sm.total_tax), color: '#6b7280' },
                      ].map((t) => tile(t, true))}
                    </div>

                    <div>
                      {sectionHead('Amount received by payment mode', `Includes advances of ${fmt(sm.total_advance)} collected before checkout. These tiles add up to Amount Received.`)}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.75rem' }}>
                        {modes.map((t) => tile({ ...t, value: fmt(t.value) }))}
                        {tile({ label: 'Refunds Paid Out', value: fmt(sm.total_refund), color: '#b91c1c', hint: 'Refunds made in this period' })}
                      </div>
                      <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: gap === 0 ? colors.success : '#dc2626' }}>
                        {gap === 0
                          ? `\u2713 Check: Amount Received ${fmt(sm.total_received)} + Discounts & Adjustments ${fmt(adjustments)} = Total credits on these bills ${fmt(sm.total_payments)}`
                          : `\u26a0 The bills' stored credits (${fmt(sm.total_payments)}) differ from their folio entries by ${fmt(gap)}. A bill may be out of date; please report this.`}
                      </div>
                    </div>

                  </>
                );
              })()}
            </>
          )}

          {/* Detail Table */}
          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>Invoice Details ({billSummary.rows.length})</span>
            </div>
            {renderTable([
              { label: 'Sl.no',     val: (r, idx) => idx + 1 },
              { label: 'Bill No',   val: (r) => r.invoice_no },
              { label: 'Bill Date', val: (r) => fmtDate(r.invoice_date) },
              { label: 'Res No',    val: (r) => r.reservation_no },
              { label: 'Room',      val: (r) => r.room_number },
              { label: 'Guest',     val: (r) => r.guest_name },
              { label: 'GST No',    val: (r) => r.customer_gstin || '-' },
              { label: 'Source',    val: (r) => r.booking_source || '-' },
              { label: 'Bill Amt',  val: (r) => fmt(r.bill_amount) },
              { label: 'Advance',   val: (r) => fmt(r.advance) },
              { label: 'Cash',      val: (r) => fmt(r.cash) },
              { label: 'Card',      val: (r) => fmt(r.card) },
              { label: 'UPI',       val: (r) => fmt(r.upi) },
              { label: 'Cheque',    val: (r) => fmt(r.cheque) },
              { label: 'Company',   val: (r) => fmt(r.company) },
              { label: 'Bank',      val: (r) => fmt(r.bank) },
              { label: 'OTA',       val: (r) => fmt(r.ota) },
              { label: 'Other',     val: (r) => fmt(r.other_payment) },
              { label: 'Received',  val: (r) => fmt(r.received) },
              { label: 'Adjustments', val: (r) => fmt(Number(r.adjustments || 0) + Number(r.transfers || 0)) },
              { label: 'Refund',    val: (r) => fmt(r.refund) },
              { label: 'Tax',       val: (r) => fmt(r.total_tax) },
              { label: 'Total',     val: (r) => fmt(r.total_charges) },
              { label: 'Balance',   val: (r) => {
                const bal = parseFloat(r.balance || 0);
                return <span style={{ color: bal > 0 ? '#dc2626' : colors.success, fontWeight: 600 }}>{fmt(bal)}</span>;
              }},
              { label: 'Status',    val: (r) => r.status_label || r.invoice_status || '-' },
            ], billSummary.rows)}
          </div>
        </div>
      )}

      {/* ── Night Audit Tariff Tab ───────────────────────────────── */}
      {activeTab === 'nightaudittariff' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {nightAuditTariffLoading && <div style={{ ...cardStyle, textAlign: 'center', color: colors.textLight }}>Loading...</div>}
          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>Room Tariff Posted by Night Audit ({nightAuditTariff.length})</span>
            </div>
            {renderTable([
              { label: 'Posting Date', val: (r) => fmtDate(r.posting_date) },
              { label: 'Room',         val: (r) => r.room_number },
              { label: 'Res No',       val: (r) => r.reservation_no },
              { label: 'Guest',        val: (r) => r.primary_guest_name || '-' },
              { label: 'Rate',         val: (r) => fmt(r.rate_amount) },
              { label: 'Tax',          val: (r) => fmt(r.tax_amount) },
              { label: 'Total',        val: (r) => fmt(Number(r.rate_amount || 0) + Number(r.tax_amount || 0)) },
              { label: 'Posted At',    val: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : '-' },
              { label: 'Stay Status',  val: (r) => r.stay_status || '-' },
              { label: 'Remarks',      val: (r) => r.remarks || '-' },
            ], nightAuditTariff, { pageSize: 50 })}
          </div>
        </div>
      )}

      {/* ── Day Summary Tab ──────────────────────────────────────── */}
      {activeTab === 'daysummary' && (() => {
        const ds = daySummary;
        const renderSection = (label, rows, totals) => {
          const data = Array.isArray(rows) ? rows : [];
          return (
            <>
              <tr style={{ background: '#e2e8f0' }}>
                <td colSpan={12} style={{ ...tdStyle, fontWeight: 700, color: colors.primary, padding: '0.55rem 0.8rem' }}>{label}</td>
              </tr>
              {data.length === 0 ? (
                <tr><td colSpan={12} style={{ ...tdStyle, textAlign: 'center', color: colors.textLight }}>No entries</td></tr>
              ) : data.map((r, idx) => (
                <tr key={`${label}-${idx}`}>
                  <td style={tdStyle}>{r.bill_number}</td>
                  <td style={tdStyle}>{fmtDate(r.bill_date)}</td>
                  <td style={tdStyle}>{r.room || '-'}</td>
                  <td style={tdStyle}>{r.guest_name || '-'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.cash)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.card)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.upi)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.company)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.cheque)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.neft)}</td>
                  <td style={tdStyle}>{r.company_name || '-'}</td>
                </tr>
              ))}
              <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
                <td style={tdStyle} colSpan={4}>{label} Total</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(totals?.cash)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(totals?.card)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(totals?.upi)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(totals?.company)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(totals?.cheque)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(totals?.neft)}</td>
                <td style={tdStyle}></td>
              </tr>
            </>
          );
        };
        // Refund rows carry one flat refund_mode + amount (unlike the other
        // three sections, which already have one column per mode) — bucket
        // each row into the matching column so it can reuse renderSection()
        // unchanged instead of a second, near-duplicate table renderer.
        const REFUND_MODE_COL = {
          CASH: 'cash', CARD: 'card', CREDIT_CARD: 'card', DEBIT_CARD: 'card', UPI: 'upi',
          COMPANY: 'company', CORPORATE: 'company', BTC: 'company', BILL_TO_COMPANY: 'company', CITY_LEDGER: 'company', CITYLEDGER: 'company',
          CHEQUE: 'cheque', NEFT: 'neft', RTGS: 'neft', IMPS: 'neft', BANK_TRANSFER: 'neft', BANK: 'neft',
        };
        const refundRows = (ds.refunds?.rows || []).map((r) => {
          const col = REFUND_MODE_COL[String(r.refund_mode || '').toUpperCase()] || 'cash';
          return {
            bill_number: r.bill_number, bill_date: r.bill_date, room: r.room, guest_name: r.guest_name,
            company_name: r.reservation_no ? `Res: ${r.reservation_no}` : (r.remarks || '-'),
            cash: col === 'cash' ? r.amount : 0, card: col === 'card' ? r.amount : 0, upi: col === 'upi' ? r.amount : 0,
            company: col === 'company' ? r.amount : 0, cheque: col === 'cheque' ? r.amount : 0, neft: col === 'neft' ? r.amount : 0,
          };
        });
        return (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {daySummaryLoading && <div style={{ ...cardStyle, textAlign: 'center', color: colors.textLight }}>Loading...</div>}
            <div style={cardStyle}>
              <div style={{ ...headerStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: colors.primary, color: '#fff', padding: '0.6rem 0.9rem' }}>
                <span>DAY SUMMARY — DETAILED FOR {fmtDate(ds.date || date)}</span>
                <button type="button" onClick={openRefundModal}
                  style={{ padding: '0.35rem 0.8rem', borderRadius: 6, border: 'none', background: '#fff', color: colors.primary, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                  ↩ New Refund
                </button>
              </div>
              <div style={tableContainerStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Bill Number</th>
                      <th style={thStyle}>Bill Date</th>
                      <th style={thStyle}>Room</th>
                      <th style={thStyle}>Guest Name</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Cash</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Credit Card</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>UPI</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Company</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Cheque</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>NEFT</th>
                      <th style={thStyle}>Company Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderSection('Frontoffice',         ds.frontoffice?.rows, ds.frontoffice?.totals)}
                    {renderSection('Advance',             ds.advance?.rows,     ds.advance?.totals)}
                    {renderSection('F&B Revenue (POS)',   ds.fnb?.rows,         ds.fnb?.totals)}
                    <tr style={{ background: colors.primary, color: '#fff', fontWeight: 800 }}>
                      <td style={{ ...tdStyle, color: '#fff' }} colSpan={4}>Grand Total (Collections)</td>
                      <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmt(ds.grand_total?.cash)}</td>
                      <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmt(ds.grand_total?.card)}</td>
                      <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmt(ds.grand_total?.upi)}</td>
                      <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmt(ds.grand_total?.company)}</td>
                      <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmt(ds.grand_total?.cheque)}</td>
                      <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmt(ds.grand_total?.neft)}</td>
                      <td style={{ ...tdStyle, color: '#fff' }}></td>
                    </tr>
                    {renderSection('Refunds', refundRows, ds.refunds?.totals)}
                    <tr style={{ background: '#b91c1c', color: '#fff', fontWeight: 800 }}>
                      <td style={{ ...tdStyle, color: '#fff' }} colSpan={10}>Net Total (Collections − Refunds)</td>
                      <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmt(ds.net_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── New Refund Modal (Day Summary tab) ──────────────────────── */}
      {refundModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget && !refundBusy) setRefundModalOpen(false); }}>
          <div style={{ width: 'min(480px,94vw)', maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: 10, padding: '1.2rem', position: 'relative' }}>
            <button onClick={() => setRefundModalOpen(false)} disabled={refundBusy}
              style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', border: '1px solid #cbd5e0', background: '#fff', color: '#334155', borderRadius: 4, padding: '0.05rem 0.4rem', fontSize: '1rem', cursor: 'pointer' }}>×</button>
            <h3 style={{ marginTop: 0, marginBottom: '0.6rem', color: colors.primary }}>↩ New Refund</h3>
            {refundError && <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{refundError}</div>}

            {!refundSelected ? (
              <>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Search Reservation No or Guest Name
                  <input value={refundSearch} onChange={(e) => setRefundSearch(e.target.value)}
                    placeholder="e.g. RES-1234 or guest name" autoFocus
                    style={{ padding: '0.5rem', border: '1px solid #cbd5e0', borderRadius: 6 }} />
                </label>
                {refundReservationsLoading && <div style={{ color: colors.textLight, fontSize: '0.85rem' }}>Loading reservations…</div>}
                {!refundReservationsLoading && refundSearch.trim() && (
                  <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                    {refundSearchResults.length === 0 ? (
                      <div style={{ padding: '0.6rem', color: colors.textLight, fontSize: '0.82rem' }}>No matching reservation with a refundable advance.</div>
                    ) : refundSearchResults.map((r) => (
                      <div key={r.reservation_id} onClick={() => setRefundSelected(r)}
                        style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '0.85rem' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
                        <b>{r.reservation_no}</b> — {r.primary_guest_name || '-'}
                        <span style={{ float: 'right', color: '#166534', fontWeight: 600 }}>₹{fmtNum(r.net_advance)} held</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.7rem', fontSize: '0.85rem' }}>
                  <div><b>{refundSelected.reservation_no}</b> — {refundSelected.primary_guest_name || '-'}</div>
                  <div style={{ color: '#166534', fontWeight: 600, marginTop: 2 }}>Net advance held: ₹{fmtNum(refundSelected.net_advance)}</div>
                  <button type="button" onClick={() => setRefundSelected(null)}
                    style={{ marginTop: 4, fontSize: '0.75rem', padding: '0.15rem 0.5rem', border: '1px solid #cbd5e0', background: '#fff', color: '#334155', borderRadius: 4, cursor: 'pointer' }}>
                    Change
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.82rem' }}>
                    Refund Amount *
                    <input type="number" min={0} step="0.01" value={refundForm.amount}
                      onChange={(e) => setRefundForm((f) => ({ ...f, amount: e.target.value }))}
                      placeholder={fmtNum(refundSelected.net_advance)}
                      style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6 }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.82rem' }}>
                    Mode
                    <select value={refundForm.payment_mode} onChange={(e) => setRefundForm((f) => ({ ...f, payment_mode: e.target.value }))}
                      style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6 }}>
                      {['CASH', 'CARD', 'UPI', 'BANK_TRANSFER', 'CHEQUE'].map((m) => <option key={m}>{m}</option>)}
                    </select>
                  </label>
                </div>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.82rem', marginBottom: '0.5rem' }}>
                  Reference No
                  <input value={refundForm.reference_no} onChange={(e) => setRefundForm((f) => ({ ...f, reference_no: e.target.value }))}
                    placeholder="Txn / UPI ref (if applicable)" style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6 }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontWeight: 600, fontSize: '0.82rem', marginBottom: '0.8rem' }}>
                  Remarks
                  <input value={refundForm.remarks} onChange={(e) => setRefundForm((f) => ({ ...f, remarks: e.target.value }))}
                    placeholder="Reason for refund" style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6 }} />
                </label>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button onClick={() => setRefundModalOpen(false)} disabled={refundBusy}
                    style={{ padding: '0.45rem 0.9rem', border: '1px solid #cbd5e0', background: '#fff', color: '#334155', borderRadius: 6, cursor: 'pointer', fontSize: '0.83rem' }}>Cancel</button>
                  <button onClick={submitNewRefund} disabled={refundBusy}
                    style={{ padding: '0.45rem 1rem', border: 'none', background: '#b91c1c', color: '#fff', borderRadius: 6, cursor: refundBusy ? 'default' : 'pointer', fontSize: '0.83rem', fontWeight: 600, opacity: refundBusy ? 0.6 : 1 }}>
                    {refundBusy ? 'Processing…' : 'Process Refund'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Day Wise Occupancy Analysis Tab ─────────────────────── */}
      {activeTab === 'occanalysis' && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {occAnalysisLoading && <div style={{ ...cardStyle, textAlign: 'center', color: colors.textLight }}>Loading...</div>}
          <div style={cardStyle}>
            <div style={{ ...headerStyle, textAlign: 'center', background: colors.primary, color: '#fff', padding: '0.6rem' }}>
              DAY WISE OCCUPANCY ANALYSIS
            </div>
            {renderTable([
              { label: 'Date',      val: (r) => fmtDate(r.date) },
              { label: 'Total',     val: (r) => r.total,            align: 'right' },
              { label: 'Blocked',   val: (r) => r.blocked,          align: 'right' },
              { label: 'Available', val: (r) => r.available,        align: 'right' },
              { label: 'Occupied',  val: (r) => r.occupied,         align: 'right' },
              { label: 'Day Use',   val: (r) => r.day_use,          align: 'right' },
              { label: 'Sold',      val: (r) => r.sold,             align: 'right' },
              { label: '% Occ',     val: (r) => fmtNum(r.pct_occ),  align: 'right' },
              { label: 'Pax',       val: (r) => r.pax,              align: 'right' },
              { label: 'Revenue',   val: (r) => fmt(r.revenue),     align: 'right' },
              { label: 'A.R.R',     val: (r) => fmt(r.arr),         align: 'right' },
              { label: 'A.R.P',     val: (r) => fmt(r.arp),         align: 'right' },
            ], occAnalysis.rows, {
              footerRow: occAnalysis.rows.length > 0 ? (
                <tr style={{ background: colors.primary, color: '#fff', fontWeight: 800 }}>
                  <td style={{ ...tdStyle, color: '#fff' }}>GRAND TOTAL</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{occAnalysis.totals?.total}</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{occAnalysis.totals?.blocked}</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{occAnalysis.totals?.available}</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{occAnalysis.totals?.occupied}</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{occAnalysis.totals?.day_use}</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{occAnalysis.totals?.sold}</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmtNum(occAnalysis.totals?.pct_occ)}</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{occAnalysis.totals?.pax}</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmt(occAnalysis.totals?.revenue)}</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmt(occAnalysis.totals?.arr)}</td>
                  <td style={{ ...tdStyle, color: '#fff', textAlign: 'right' }}>{fmt(occAnalysis.totals?.arp)}</td>
                </tr>
              ) : null,
            })}
          </div>
        </div>
      )}

      {/* ── Block Room Tab ──────────────────────────────────────── */}
      {activeTab === 'blockroom' && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {blockRoomLoading && <div style={{ ...cardStyle, textAlign: 'center', color: colors.textLight }}>Loading...</div>}
          {blockRoom.summary && blockRoom.summary.total !== undefined && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
              {[
                { label: 'Total Blocks', value: blockRoom.summary.total, color: colors.primary },
                { label: 'Currently Open', value: blockRoom.summary.open, color: '#dc2626' },
                { label: 'Released', value: blockRoom.summary.released, color: colors.success },
              ].map((s) => (
                <div key={s.label} style={{ background: '#fff', borderRadius: '8px', padding: '1rem', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
                  <div style={{ fontSize: '0.78rem', color: colors.textLight, marginBottom: '0.3rem' }}>{s.label}</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}
          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>Block Room Report ({blockRoom.rows.length})</span>
            </div>
            {renderTable([
              { label: 'Sl.no',       val: (r, idx) => idx + 1 },
              { label: 'Room No',     val: (r) => r.room_number || '-' },
              { label: 'Room Type',   val: (r) => r.room_type_name || '-' },
              { label: 'From Date',   val: (r) => fmtDate(r.from_date) },
              { label: 'To Date',     val: (r) => r.to_date ? fmtDate(r.to_date) : <span style={{ color: '#dc2626', fontWeight: 600 }}>(open)</span> },
              { label: 'Block',       val: (r) => r.block_reason || '-' },
              { label: 'Remarks',     val: (r) => r.remarks || '-' },
              { label: 'Released by', val: (r) => r.released_by_name || '-' },
              { label: 'Released on', val: (r) => r.released_at ? fmtDate(r.released_at) : '-' },
              { label: 'Added by',    val: (r) => r.added_by_name || '-' },
              { label: 'Added on',    val: (r) => fmtDate(r.added_at) },
            ], blockRoom.rows)}
          </div>
        </div>
      )}

      {activeTab === 'advance' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
          {/* Summary bar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
            {[
              { label: 'Total Advance', value: fmt(advanceReport.total_advance), color: '#1d4ed8' },
              { label: 'Total Refund', value: fmt(advanceReport.total_refund), color: '#b91c1c' },
              { label: 'Net Balance', value: fmt(advanceReport.net), color: advanceReport.net >= 0 ? '#15803d' : '#b91c1c' },
              { label: 'Transactions', value: advanceReport.transactions.length, color: colors.primary }
            ].map((s) => (
              <div key={s.label} style={{ background: '#fff', borderRadius: '8px', padding: '1rem', border: `1px solid ${colors.border}`, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '0.78rem', color: colors.textLight, marginBottom: '0.3rem' }}>{s.label}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Transaction table */}
          <div style={cardStyle}>
            <div style={headerStyle}>Advance & Refund Transactions</div>
            {advanceLoading ? (
              <div style={{ color: colors.textLight, padding: '2rem', textAlign: 'center' }}>Loading...</div>
            ) : (
              renderTable([
                { label: 'Txn Date', val: (r) => fmtDateTime(r.txn_date) },
                {
                  label: 'Type', val: (r) => (
                    <span style={{
                      display: 'inline-block', padding: '0.2rem 0.55rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700,
                      background: r.txn_type === 'ADVANCE' ? '#eff6ff' : '#fef2f2',
                      color: r.txn_type === 'ADVANCE' ? '#1d4ed8' : '#b91c1c',
                      border: `1px solid ${r.txn_type === 'ADVANCE' ? '#bfdbfe' : '#fecaca'}`
                    }}>{r.txn_type}</span>
                  )
                },
                { label: 'Reservation No', val: (r) => r.reservation_no || '-' },
                { label: 'Guest Name', val: (r) => r.primary_guest_name || '-' },
                { label: 'Mobile', val: (r) => r.mobile_number || '-' },
                { label: 'Amount', val: (r) => fmt(r.amount) },
                { label: 'Payment Mode', val: (r) => r.payment_mode || '-' },
                { label: 'Reference No', val: (r) => r.reference_no || '-' },
                { label: 'Remarks', val: (r) => r.remarks || '-' },
                { label: 'Arrival', val: (r) => r.arrival_date || '-' },
                { label: 'Departure', val: (r) => r.departure_date || '-' },
                { label: 'Res. Status', val: (r) => r.reservation_status || '-' },
                { label: 'Created By', val: (r) => r.created_by || '-' }
              ], advanceReport.transactions)
            )}
          </div>
        </div>
      )}

      {activeTab === 'noshow' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>No-Show & Cancellation Report ({noshowRows.length})</span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select
                  value={noshowFilter}
                  onChange={(e) => setNoshowFilter(e.target.value)}
                  style={{ padding: '0.4rem', borderRadius: '4px', border: `1px solid ${colors.border}`, fontSize: '0.85rem' }}
                >
                  <option value="ALL">All</option>
                  <option value="CANCELLED">Cancelled Only</option>
                  <option value="NO_SHOW">No-Show Only</option>
                </select>
                <button onClick={fetchNoshowReport} disabled={noshowLoading} style={{ padding: '0.4rem 0.8rem', background: colors.accent, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem' }}>
                  {noshowLoading ? 'Loading...' : 'Search'}
                </button>
                {noshowRows.length > 0 && (
                  <button onClick={exportNoshow} style={{ padding: '0.4rem 0.8rem', background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem' }}>Export CSV</button>
                )}
              </div>
            </div>
            {/* Summary */}
            {noshowRows.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.8rem', marginBottom: '1rem' }}>
                {[
                  { label: 'Total Records', value: noshowRows.length, color: colors.primary },
                  { label: 'Cancelled', value: noshowRows.filter(r => r.status === 'CANCELLED').length, color: '#b91c1c' },
                  { label: 'No-Show', value: noshowRows.filter(r => r.status === 'NO_SHOW').length, color: 'var(--theme-warning)' },
                  { label: 'Est. Lost Revenue', value: fmt(noshowRows.reduce((s, r) => s + Number(r.estimated_lost_revenue || 0), 0)), color: '#b91c1c' }
                ].map((s) => (
                  <div key={s.label} style={{ background: '#f8f9fa', borderRadius: '8px', padding: '0.8rem', textAlign: 'center', border: `1px solid ${colors.border}` }}>
                    <div style={{ fontSize: '0.78rem', color: colors.textLight }}>{s.label}</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
            )}
            {noshowLoading ? (
              <div style={{ color: colors.textLight, padding: '2rem', textAlign: 'center' }}>Loading...</div>
            ) : (
              renderTable([
                { label: 'Res No', val: (r) => r.reservation_no || '-' },
                { label: 'Tot. Rooms', val: (r) => r.tot_no_rooms || '-' },
                { label: 'Guest', val: (r) => r.primary_guest_name || '-' },
                { label: 'Mobile', val: (r) => r.mobile || '-' },
                {
                  label: 'Status', val: (r) => (
                    <span style={{
                      padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700,
                      background: r.status === 'CANCELLED' ? '#fef2f2' : '#fffbeb',
                      color: r.status === 'CANCELLED' ? '#b91c1c' : 'var(--theme-warning)',
                      border: `1px solid ${r.status === 'CANCELLED' ? '#fecaca' : '#fde68a'}`
                    }}>{r.status}</span>
                  )
                },
                { label: 'Booking Date', val: (r) => fmtDate(r.booking_date) },
                { label: 'Arrival', val: (r) => fmtDate(r.arrival_date) },
                { label: 'Departure', val: (r) => fmtDate(r.departure_date) },
                { label: 'Nights', val: (r) => r.total_nights || '-' },
                { label: 'Meal Plan', val: (r) => mealPlanOf(r) },
                { label: 'Source', val: (r) => r.booking_source || '-' },
                { label: 'Pax', val: (r) => r.pax || '-' },
                { label: 'Est. Lost Revenue', val: (r) => fmt(r.estimated_lost_revenue) },
                { label: 'Cancelled At', val: (r) => fmtDateTime(r.cancelled_at) }
              ], noshowRows)
            )}
          </div>
        </div>
      )}

      {activeTab === 'guesthistory' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
          <div style={cardStyle}>
            <div style={headerStyle}>
              <span>Guest History / Booking Report ({guestHistoryRows.length})</span>
              {guestHistoryRows.length > 0 && (
                <button onClick={exportGuestHistory} style={{ padding: '0.4rem 0.8rem', background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem' }}>Export CSV</button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.6rem', marginBottom: '0.9rem' }}>
              <label style={{ fontSize: '0.82rem', color: colors.text }}>
                Guest Name
                <input
                  type="text"
                  value={guestHistoryFilters.guest_name}
                  onChange={(e) => setGuestHistoryFilters(f => ({ ...f, guest_name: e.target.value }))}
                  placeholder="Search by name"
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
                />
              </label>
              <label style={{ fontSize: '0.82rem', color: colors.text }}>
                Mobile
                <input
                  type="text"
                  value={guestHistoryFilters.mobile}
                  onChange={(e) => setGuestHistoryFilters(f => ({ ...f, mobile: e.target.value }))}
                  placeholder="Search by mobile"
                  style={{ width: '100%', padding: '0.45rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.9rem' }}>
              <button onClick={fetchGuestHistory} disabled={guestHistoryLoading} style={{ padding: '0.5rem 0.9rem', borderRadius: '4px', border: 'none', background: colors.accent, color: '#fff', cursor: 'pointer' }}>
                {guestHistoryLoading ? 'Loading...' : 'Search'}
              </button>
              <button
                onClick={() => {
                  setGuestHistoryFilters({ guest_name: '', mobile: '' });
                  fetchGuestHistory();
                }}
                style={{ padding: '0.5rem 0.9rem', borderRadius: '4px', border: `1px solid ${colors.accent}`, background: '#e8f0fe', color: colors.accent, fontWeight: 600, cursor: 'pointer' }}
              >
                Reset
              </button>
            </div>
            {guestHistoryLoading ? (
              <div style={{ color: colors.textLight, padding: '2rem', textAlign: 'center' }}>Loading...</div>
            ) : (
              renderTable([
                { label: 'Res No', val: (r) => r.reservation_no || '-' },
                { label: 'Guest', val: (r) => r.guest_name || '-' },
                { label: 'Mobile', val: (r) => r.mobile || '-' },
                { label: 'Email', val: (r) => r.email || '-' },
                {
                  label: 'Status', val: (r) => {
                    const statusColors = {
                      'CONFIRMED': { bg: '#eff6ff', color: '#1d4ed8' },
                      'GUARANTEED': { bg: '#eff6ff', color: '#1d4ed8' },
                      'CHECKED_IN': { bg: '#dcfce7', color: '#15803d' },
                      'CHECKED_OUT': { bg: '#f3f4f6', color: '#6b7280' },
                      'CANCELLED': { bg: '#fef2f2', color: '#b91c1c' },
                      'NO_SHOW': { bg: '#fffbeb', color: 'var(--theme-warning)' }
                    };
                    const sc = statusColors[r.status] || { bg: '#f3f4f6', color: 'var(--theme-text)' };
                    return <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, background: sc.bg, color: sc.color }}>{r.status}</span>;
                  }
                },
                { label: 'Arrival', val: (r) => fmtDate(r.arrival_date) },
                { label: 'Departure', val: (r) => fmtDate(r.departure_date) },
                { label: 'Nights', val: (r) => r.total_nights || '-' },
                { label: 'Room Types', val: (r) => r.room_types || '-' },
                { label: 'Rooms', val: (r) => r.room_numbers || '-' },
                { label: 'Meal Plan', val: (r) => mealPlanOf(r) },
                { label: 'Room Charge', val: (r) => fmt(r.total_room_charge) },
                { label: 'Source', val: (r) => r.booking_source || '-' },
                { label: 'Pax', val: (r) => r.pax || '-' },
                { label: 'Corporate', val: (r) => r.corporate_name || '-' },
                { label: 'Agent', val: (r) => r.travel_agent_name || '-' },
                { label: 'Booking Date', val: (r) => fmtDate(r.booking_date) },
                { label: 'Check-in', val: (r) => fmtDateTime(r.actual_check_in) },
                { label: 'Check-out', val: (r) => fmtDateTime(r.actual_check_out) }
              ], guestHistoryRows)
            )}
          </div>
        </div>
      )}

      {folioModal.open && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.5)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem'
        }}>
          <div style={{
            width: 'min(1000px, 96vw)',
            maxHeight: '90vh',
            overflow: 'auto',
            background: '#fff',
            borderRadius: '10px',
            border: `1px solid ${colors.border}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.22)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem 1rem', borderBottom: `1px solid ${colors.border}` }}>
              <div style={{ fontWeight: 700, color: colors.primary }}>
                Folio Details - {folioModal.row?.reservation_no || '-'} - {folioModal.row?.guest_name || '-'}
              </div>
              <button onClick={closeFolioModal} style={{ border: 'none', background: 'transparent', fontSize: '1rem', cursor: 'pointer', color: colors.text }}>
                Close
              </button>
            </div>
            <div style={{ padding: '1rem' }}>
              {folioModal.loading && <div style={{ color: colors.textLight }}>Loading folio...</div>}
              {!folioModal.loading && folioModal.error && <div style={{ color: '#b91c1c' }}>{folioModal.error}</div>}
              {!folioModal.loading && !folioModal.error && folioModal.data && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.6rem', marginBottom: '0.9rem' }}>
                    <div><b>Folio ID:</b> {folioModal.data.folio?.folio_id || '-'}</div>
                    <div><b>Status:</b> {folioModal.data.folio?.folio_status || '-'}</div>
                    <div><b>Total Debit:</b> {fmt(folioModal.data.folio?.total_debit || 0)}</div>
                    <div><b>Total Credit:</b> {fmt(folioModal.data.folio?.total_credit || 0)}</div>
                    <div><b>Balance:</b> {fmt(folioModal.data.folio?.balance || 0)}</div>
                    <div><b>Room:</b> {folioModal.data.room_number || '-'}</div>
                  </div>
                  {renderTable([
                    { label: 'Date', val: (r) => fmtDate(r.posting_date || r.created_at) },
                    { label: 'Posting Type', val: (r) => r.posting_type || '-' },
                    { label: 'Revenue', val: (r) => r.revenue_desc || r.revenue_code || '-' },
                    { label: 'Debit', val: (r) => fmt(r.debit_amount) },
                    { label: 'Credit', val: (r) => fmt(r.credit_amount) },
                    { label: 'GST', val: (r) => fmt(Number(r.cgst_amount || 0) + Number(r.sgst_amount || 0) + Number(r.igst_amount || 0)) },
                    { label: 'Remarks', val: (r) => r.remarks || '-' }
                  ], Array.isArray(folioModal.data.lines) ? folioModal.data.lines : [])}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pre-Check-In Link Modal */}
      {preCheckinModal.open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, width: '95%', maxWidth: 520, padding: '1.5rem', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: colors.primary }}>
                Pre-Check-In Link — {preCheckinModal.row?.reservation_no}
              </div>
              <button onClick={closePreCheckinModal} style={{ border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: colors.text }}>X</button>
            </div>

            <div style={{ fontSize: '0.88rem', marginBottom: '0.8rem', color: colors.text }}>
              <div><strong>Guest:</strong> {preCheckinModal.row?.guest_name || '-'}</div>
              <div><strong>Mobile:</strong> {preCheckinModal.row?.mobile || '-'}</div>
              <div><strong>Room Type:</strong> {preCheckinModal.row?.room_type || '-'}</div>
              <div><strong>Arrival:</strong> {fmtDate(date)} &nbsp; <strong>Status:</strong> {preCheckinModal.row?.status || '-'}</div>
            </div>

            {preCheckinModal.error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '0.5rem 0.8rem', marginBottom: '0.8rem', color: '#dc2626', fontSize: '0.82rem' }}>
                {preCheckinModal.error}
              </div>
            )}

            {preCheckinModal.loading ? (
              <div style={{ textAlign: 'center', padding: '1rem', color: colors.textLight }}>Loading...</div>
            ) : preCheckinModal.sent ? (
              <div>
                {preCheckinModal.shortUrl && (
                  <div style={{ marginBottom: '0.6rem' }}>
                    <div style={{ fontSize: '0.78rem', color: colors.textLight, marginBottom: '0.2rem', fontWeight: 600 }}>Shortened URL (share with guest):</div>
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '0.6rem', fontSize: '0.88rem', wordBreak: 'break-all', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <a href={preCheckinModal.shortUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', flex: 1 }}>
                        {preCheckinModal.shortUrl}
                      </a>
                      <button onClick={() => { navigator.clipboard.writeText(preCheckinModal.shortUrl); }}
                        style={{ border: '1px solid #cbd5e1', borderRadius: 4, padding: '0.25rem 0.6rem', fontSize: '0.73rem', cursor: 'pointer', background: '#fff', whiteSpace: 'nowrap', fontWeight: 600 }}>
                        Copy
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: '0.6rem' }}>
                  <div style={{ fontSize: '0.78rem', color: colors.textLight, marginBottom: '0.2rem', fontWeight: 600 }}>Full URL:</div>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.6rem', fontSize: '0.78rem', wordBreak: 'break-all', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <a href={preCheckinModal.fullUrl || preCheckinModal.link} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', flex: 1 }}>
                      {preCheckinModal.fullUrl || preCheckinModal.link}
                    </a>
                    <button onClick={() => { navigator.clipboard.writeText(preCheckinModal.fullUrl || preCheckinModal.link); }}
                      style={{ border: '1px solid #cbd5e1', borderRadius: 4, padding: '0.25rem 0.6rem', fontSize: '0.73rem', cursor: 'pointer', background: '#fff', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      Copy
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                  <button onClick={closePreCheckinModal}
                    style={{ padding: '0.5rem 1.2rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: colors.text, cursor: 'pointer' }}>
                    Close
                  </button>
                  <button onClick={sendPreCheckinFromModal} disabled={preCheckinModal.sending}
                    style={{ padding: '0.5rem 1rem', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: preCheckinModal.sending ? 'wait' : 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                    {preCheckinModal.sending ? 'Resending...' : 'Resend New Link'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: '0.82rem', color: colors.textLight, margin: '0 0 1rem' }}>
                  This will generate a pre-check-in link and mark it as sent. The guest can use this link to fill their ID and compliance details before arrival.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button onClick={closePreCheckinModal}
                    style={{ padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: colors.text, cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={sendPreCheckinFromModal} disabled={preCheckinModal.sending}
                    style={{ padding: '0.5rem 1.2rem', borderRadius: 6, border: 'none', background: '#059669', color: '#fff', cursor: preCheckinModal.sending ? 'wait' : 'pointer', fontWeight: 600 }}>
                    {preCheckinModal.sending ? 'Sending...' : 'Send Pre-Check-In Link'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PmsReports;
